// Main JavaScript entry point for SLJ theme

// ─── Cart ──────────────────────────────────────────────────────────────────
// Vanilla cart module using Shopify's Sectioned Rendering API. Single source
// of truth: the server-rendered cart section HTML. After every cart mutation
// we swap the inner HTML of .transparent-cart-wrapper and re-run the upsell
// renderer from project.js if available.
//
// Stacking: when the cart is open we add .active to .transparent-cart-wrapper.
// The wrapper (a direct sibling of <header>) gets position: relative;
// z-index: 9999 via CSS, which puts the entire cart layer above the header.
//
// Public API (window.SLJCart) is also exposed so legacy code paths in
// project.js (the upsell button binding) can continue to work.

(function () {
  var ROOT =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
    "/";
  var WRAPPER_SEL = ".transparent-cart-wrapper";
  var CART_SEL = "#cart";
  var COUNT_SEL = "#cart-count";

  var inflight = false;

  // ── Fetch helper ─────────────────────────────────────────────────────────
  function postCart(path, body) {
    var payload = Object.assign({}, body, { sections: "cart" });
    return fetch(ROOT + path, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) {
          var err = new Error(data.description || data.message || "Cart error");
          err.data = data;
          throw err;
        }
        return data;
      });
    });
  }

  // ── DOM helpers ──────────────────────────────────────────────────────────
  function setInflight(state) {
    inflight = state;
    if (state) document.body.setAttribute("data-cart-inflight", "");
    else document.body.removeAttribute("data-cart-inflight");
  }

  function getWrapper() {
    return document.querySelector(WRAPPER_SEL);
  }

  function updateBadge(count) {
    var badge = document.querySelector(COUNT_SEL);
    if (badge && typeof count === "number") badge.textContent = String(count);
  }

  // Count total units across line items in a rendered cart HTML string.
  // Used for /cart/add.js responses, which don't return cart.item_count.
  function countFromHTML(html) {
    try {
      var doc = new DOMParser().parseFromString(html, "text/html");
      var qs = doc.querySelectorAll(".cart-item .q-value");
      var total = 0;
      qs.forEach(function (el) {
        total += parseInt(el.textContent, 10) || 0;
      });
      return total;
    } catch (e) {
      return null;
    }
  }

  // Animate the free-shipping bar's fill from `fromWidth`% to the
  // freshly-rendered target. The section re-render replaces the bar at its
  // new width, so we snap it back to the old width and let CSS transition it
  // forward.
  function animateShippingFill(fromWidth) {
    var wrapper = getWrapper();
    var bar = wrapper && wrapper.querySelector("#shipping-bar");
    if (!bar) return;
    var fill = bar.querySelector(".shipping-bar-meter-fill");
    if (!fill) return;
    var target = parseFloat(bar.getAttribute("data-fill-width")) || 0;
    if (target > 100) target = 100;
    var from =
      typeof fromWidth === "number" && !isNaN(fromWidth) ? fromWidth : 0;

    // Snap to the start width with no transition…
    fill.style.transition = "none";
    fill.style.width = from + "%";
    // …force a reflow, then release the CSS transition toward the target.
    void fill.offsetWidth;
    fill.style.transition = "";
    fill.style.width = target + "%";
  }

  function swapCartHTML(html) {
    var wrapper = getWrapper();
    if (!wrapper) return;
    var wasOpen = document.body.classList.contains("nav-open");
    // Read the outgoing fill width so the new bar can animate from it.
    var oldBar = wrapper.querySelector("#shipping-bar");
    var oldFill = oldBar
      ? parseFloat(oldBar.getAttribute("data-fill-width"))
      : 0;

    // Preserve the "This is a gift" toggle across the re-render. The server
    // renders it checked only when cart.note is set, so a toggled-but-not-yet-
    // saved gift (checkbox on, note still being typed) would be wiped on every
    // cart update — which made the reveal feel intermittent.
    var oldGift = wrapper.querySelector("#is-gift");
    var giftWasChecked = oldGift ? oldGift.checked : false;
    var oldNoteInput = wrapper.querySelector("#g-note");
    var noteWasTyped = oldNoteInput ? oldNoteInput.value : "";

    // The overlay is a sibling of the cart section inside the wrapper
    // (theme.liquid), but the Section Rendering API HTML we inject contains
    // only the cart — so this innerHTML swap deletes the overlay, and it never
    // shows on the next open. Preserve the existing node across the swap, and
    // recreate it if a previous swap already dropped it.
    var overlay = wrapper.querySelector(".cart-overlay");

    wrapper.innerHTML = html;

    if (!wrapper.querySelector(".cart-overlay")) {
      if (!overlay) {
        overlay = document.createElement("div");
        overlay.className = "cart-overlay";
        overlay.setAttribute("aria-hidden", "true");
      }
      wrapper.insertBefore(overlay, wrapper.firstChild);
    }

    if (giftWasChecked) {
      var newGift = wrapper.querySelector("#is-gift");
      var newGiftNote = wrapper.querySelector("#gift-note");
      var newNoteInput = wrapper.querySelector("#g-note");
      if (newGift) newGift.checked = true;
      if (newGiftNote) newGiftNote.classList.remove("hide");
      if (newNoteInput && noteWasTyped) newNoteInput.value = noteWasTyped;
    }

    if (wasOpen) {
      var cart = document.querySelector(CART_SEL);
      if (cart) {
        cart.classList.add("nav-active");
        cart.classList.add("nav-open");
      }
    }
    animateShippingFill(isNaN(oldFill) ? 0 : oldFill);
    // Re-run the upsell renderer if project.js has defined it.
    if (typeof window.renderRecommendations === "function") {
      window.renderRecommendations();
    }
  }

  // ── Klaviyo newsletter flyout, paired with the open cart ────────────────
  // Special case: the newsletter flyout is force-opened when the cart drawer
  // opens and dismissed when it closes. Desktop only — below $mq-tablet-min
  // the drawer goes full-width (#cart.nav in themes/_layout.scss), so there's
  // no room beside it.
  //
  // Klaviyo pins the flyout to the bottom-RIGHT, the same corner the drawer
  // occupies, so themes/_klaviyo-flyout.scss flips it to the left. Every class
  // Klaviyo emits is build-hashed (kl-private-reset-css-…), and the
  // position:fixed wrapper is an ANCESTOR of the only stable node — the form's
  // data-testid — so CSS can't reach it on its own. Walk up from the form and
  // tag the wrapper for the stylesheet to hook.
  // "Newsletter Popup Cart" — this row's OWN Klaviyo form, set to trigger-only
  // display so it never opens on its own. Distinct from "Newsletter Popup 2026"
  // (XwiXWT), which the footer button opens and which still runs as the general
  // newsletter popup — don't collapse the two back together.
  var KL_CART_FORM_ID = "TfS4Se";
  var KL_FORM_SEL = '[data-testid="klaviyo-form-' + KL_CART_FORM_ID + '"]';
  var KL_TAG = "slj-kl-cart-flyout";
  var KL_FADING = "slj-kl-fading";
  var KL_SUB_KEY = "slj:newsletter-subscribed";
  var KL_MIN_WIDTH = 770; // one past $mq-tablet-min (769px)
  var KL_FADE_MS = 600; // keep in sync with $kl-flyout-fade in the stylesheet
  // Fade, plus a buffer for Klaviyo's own close animation to finish before we
  // release the element back to it. Costs nothing visually — the flyout is
  // already at opacity 0 for the whole buffer. See klDismiss.
  var KL_RELEASE_MS = 1000;
  // Hold the reveal until the drawer has finished sliding. Matches #cart.nav's
  // `left 0.3s linear` in themes/_layout.scss.
  var KL_OPEN_DELAY_MS = 300;
  var klOpenedAt = 0;
  var klPoll = null;
  var klFadeTimer = null;
  var klWanted = false;

  function klFindFixedWrapper() {
    var form = document.querySelector(KL_FORM_SEL);
    if (!form) return null;
    for (
      var n = form.parentElement;
      n && n !== document.body;
      n = n.parentElement
    ) {
      if (window.getComputedStyle(n).position === "fixed") return n;
    }
    return null;
  }

  // Already on the list? Don't re-pitch them on every cart open.
  //
  // Two signals, because neither covers everyone on its own: our own flag is
  // exact but only knows about submits since this shipped, and isIdentified()
  // catches profiles Klaviyo already knew about. Resolves false on any error
  // so a Klaviyo hiccup shows the form rather than silently killing it.
  function klIsSubscribed() {
    var flagged = false;
    try {
      flagged = window.localStorage.getItem(KL_SUB_KEY) === "1";
    } catch (e) {}
    if (flagged) return Promise.resolve(true);
    var k = window.klaviyo;
    if (!k || typeof k.isIdentified !== "function") {
      return Promise.resolve(false);
    }
    try {
      return Promise.resolve(k.isIdentified()).then(
        function (v) {
          return !!v;
        },
        function () {
          return false;
        },
      );
    } catch (e) {
      return Promise.resolve(false);
    }
  }

  // Klaviyo's public form event. A submit of ANY newsletter form means they're
  // on the list, so the cart popup has nothing left to ask for. All three sign
  // up to the same list: this one, the general flyout the footer opens, and the
  // embed on /pages/newsletter-signup. Matching only the cart form would keep
  // re-pitching someone who just subscribed through one of the others.
  // Persisted rather than session-scoped.
  var KL_NEWSLETTER_FORM_IDS = [
    KL_CART_FORM_ID,
    "XwiXWT", // "Newsletter Popup 2026" — footer button (snippets/footer.liquid)
    "WK6GQV", // inline embed on /pages/newsletter-signup (snippets/newsletter.liquid)
  ];
  window.addEventListener("klaviyoForms", function (e) {
    var d = e && e.detail;
    if (!d || KL_NEWSLETTER_FORM_IDS.indexOf(d.formId) === -1) return;
    if (d.type === "submit" || d.type === "redactedSubmit") {
      try {
        window.localStorage.setItem(KL_SUB_KEY, "1");
      } catch (err) {}
    }
  });

  // Klaviyo exposes no closeForm API — its own close button is the only
  // supported way out. openForm ignores the dismissal that results (same
  // reason footer.liquid can always reopen it).
  function klHandBackToKlaviyo(wrap) {
    var btn = wrap.querySelector(".klaviyo-close-form");
    if (btn) btn.click();
  }

  function klReveal(wrap) {
    clearTimeout(klFadeTimer);
    // Tag the moment Klaviyo mounts, not when we intend to show it: the class
    // pins the flyout left, kills its entrance motion and holds it at
    // opacity 0, so its own bottom-right slide-in is never seen during the
    // wait below.
    wrap.classList.add(KL_TAG, KL_FADING);
    // Then fade in only once the drawer has finished sliding. Klaviyo often
    // takes longer than the drawer to mount (script load, form fetch), in
    // which case the remainder is 0 and it fades in straight away — the
    // drawer is already open by then either way.
    var remaining = Math.max(0, KL_OPEN_DELAY_MS - (Date.now() - klOpenedAt));
    klFadeTimer = setTimeout(function () {
      if (!klWanted) return;
      // Paint the transparent, already-repositioned frame first — a class
      // added in the same frame as the one that styles it won't transition.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          if (klWanted) wrap.classList.remove(KL_FADING);
        });
      });
    }, remaining);
  }

  function klDismiss() {
    var wrap = document.querySelector("." + KL_TAG) || klFindFixedWrapper();
    if (!wrap) return;
    if (!wrap.classList.contains(KL_TAG)) {
      // Mounted after the cart had already closed, so it was never shown —
      // hand it straight back with no fade.
      klHandBackToKlaviyo(wrap);
      return;
    }
    // Fade out and start Klaviyo's own close AT THE SAME TIME, holding our
    // overrides on throughout.
    //
    // Klaviyo's inline transform while the form is OPEN is scale(1), not the
    // scale(0) of its hidden state. So releasing KL_TAG before its close has
    // settled snaps the flyout back to full opacity in the RIGHT corner, and
    // its slide-out then plays in full view. Keeping `transform: none` and
    // `opacity: 0` on for the whole handoff hides all of that; by
    // KL_RELEASE_MS Klaviyo has written its closed scale(0) and it's safe to
    // let go.
    wrap.classList.add(KL_FADING);
    klHandBackToKlaviyo(wrap);
    clearTimeout(klFadeTimer);
    klFadeTimer = setTimeout(function () {
      // Cart reopened during the handoff — klReveal has taken over.
      if (klWanted) return;
      wrap.classList.remove(KL_TAG, KL_FADING);
    }, KL_RELEASE_MS);
  }

  function openCartFlyout() {
    if (window.innerWidth < KL_MIN_WIDTH) return;
    klWanted = true;
    klOpenedAt = Date.now();
    klIsSubscribed().then(function (subscribed) {
      // The drawer can close while the subscribed check is resolving.
      if (subscribed || !klWanted) return;
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(["openForm", KL_CART_FORM_ID]);
      // Klaviyo mounts asynchronously and re-mounts on every open (closing it
      // unmounts the form node entirely), so the wrapper has to be re-tagged
      // each time. Poll for up to ~2s rather than observing the whole document.
      clearInterval(klPoll);
      var tries = 0;
      klPoll = setInterval(function () {
        var wrap = klFindFixedWrapper();
        if (wrap) {
          clearInterval(klPoll);
          // Honour the latest intent, not the one that started the poll.
          if (klWanted) klReveal(wrap);
          else klDismiss();
        } else if (++tries > 40) {
          clearInterval(klPoll);
        }
      }, 50);
    });
  }

  function closeCartFlyout() {
    // Any poll still in flight reads klWanted and dismisses the flyout itself
    // if Klaviyo mounts it after this point, so it's left running.
    klWanted = false;
    klDismiss();
  }

  // Resizing below the breakpoint with both on screen would drop the flyout
  // on top of the now full-width drawer, so close it.
  window.addEventListener("resize", function () {
    if (klWanted && window.innerWidth < KL_MIN_WIDTH) closeCartFlyout();
  });

  function openDrawer() {
    var cart = document.querySelector(CART_SEL);
    if (!cart) return;
    // Close the mobile menu if it's open (they shouldn't overlap).
    document.body.classList.remove("menu-open");
    // Lift the wrapper above the header by giving it a stacking context.
    var wrapper = getWrapper();
    if (wrapper) wrapper.classList.add("active");
    cart.classList.add("nav-active");
    openCartFlyout();
    // double rAF so the browser paints the initial off-screen position
    // before transitioning to left: 50vw, ensuring the slide animates
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        cart.classList.add("nav-open");
        document.body.classList.add("nav-open");
      });
    });
  }

  function closeDrawer() {
    var cart = document.querySelector(CART_SEL);
    var wrapper = getWrapper();
    closeCartFlyout();
    document.body.classList.remove("nav-open");
    if (cart) cart.classList.remove("nav-open");
    // Match the 0.3s left transition before fully hiding
    setTimeout(function () {
      if (cart) cart.classList.remove("nav-active");
      // Drop the wrapper out of the stacking context once the cart is hidden,
      // so it doesn't sit on top of other UI when closed.
      if (wrapper) wrapper.classList.remove("active");
    }, 300);
  }

  function showError(message) {
    var cart = document.querySelector(CART_SEL);
    if (!cart) {
      console.error("Cart error:", message);
      return;
    }
    var existing = cart.querySelector(".cart-error");
    if (existing) existing.remove();
    var err = document.createElement("div");
    err.className = "cart-error";
    err.setAttribute("role", "alert");
    err.textContent = message;
    var wrapper = cart.querySelector(".cart-wrapper");
    if (wrapper) wrapper.insertBefore(err, wrapper.firstChild);
    else cart.appendChild(err);
    setTimeout(function () {
      err.remove();
    }, 4000);
  }

  // ── Public API ───────────────────────────────────────────────────────────
  function addItem(variantId, quantity, properties) {
    if (inflight) return Promise.resolve();
    if (!variantId) return Promise.reject(new Error("Missing variant id"));
    var item = { id: variantId, quantity: parseInt(quantity, 10) || 1 };
    if (properties && Object.keys(properties).length)
      item.properties = properties;

    setInflight(true);
    return postCart("cart/add.js", { items: [item] })
      .then(function (data) {
        if (data.sections && data.sections.cart) {
          swapCartHTML(data.sections.cart);
          var count = countFromHTML(data.sections.cart);
          if (count !== null) updateBadge(count);
        }
        openDrawer();
      })
      .catch(function (err) {
        showError(err.message);
        openDrawer();
      })
      .then(function () {
        setInflight(false);
      });
  }

  function changeItem(variantId, quantity) {
    if (inflight) return Promise.resolve();
    if (!variantId) return Promise.reject(new Error("Missing variant id"));

    setInflight(true);
    return postCart("cart/change.js", {
      id: variantId,
      quantity: parseInt(quantity, 10) || 0,
    })
      .then(function (data) {
        if (data.sections && data.sections.cart)
          swapCartHTML(data.sections.cart);
        if (typeof data.item_count === "number") updateBadge(data.item_count);
      })
      .catch(function (err) {
        showError(err.message);
      })
      .then(function () {
        setInflight(false);
      });
  }

  function addUpsellFromItem(upItem) {
    if (!upItem || inflight) return;
    var uvid = upItem.getAttribute("data-variant-id");
    if (uvid) addItem(uvid, 1);
  }

  // ── PLP swatch image revert ──────────────────────────────────────────────
  // After a swatch swaps a card's image (see the swatch handler below), revert
  // the card to its original thumbnail + hover image 3s after the user stops
  // interacting with it:
  //   - desktop: 3s after the pointer leaves the card
  //   - touch:   3s after the last tap (no hover; a tap fires pointerover then
  //              pointerout when the finger lifts, starting the countdown)
  // pointerover/out cover both: any interaction cancels a pending revert, and
  // leaving / a tap lifting (re)starts the 3s timer.
  var REVERT_DELAY = 3000;
  var cardOriginals = new WeakMap(); // thumb -> pristine state (set on first swap)
  var revertTimers = new WeakMap(); // thumb -> pending timeout id

  function captureCardOriginal(thumb) {
    if (cardOriginals.has(thumb)) return; // already snapshotted; still pristine
    var img = thumb.querySelector("img.thumb-image-img");
    var hoverImg = thumb.querySelector(".hover-file-image");
    var hoverVid = thumb.querySelector(".hover-file-video");
    cardOriginals.set(thumb, {
      src: img ? img.getAttribute("src") : null,
      srcset: img ? img.getAttribute("srcset") : null,
      hoverBg: hoverImg ? hoverImg.style.backgroundImage : null,
      hoverVidDisplay: hoverVid ? hoverVid.style.display : null,
      variantId: thumb.getAttribute("data-variant-id"),
      activeSwatch: thumb.querySelector(".variant-swatch.active") || null,
    });
  }

  function revertCard(thumb) {
    var o = cardOriginals.get(thumb);
    if (!o) return;
    var img = thumb.querySelector("img.thumb-image-img");
    if (img) {
      // Restore srcset first, then src, so the browser re-picks from the
      // original responsive set rather than keeping the variant's single src.
      if (o.srcset != null) img.setAttribute("srcset", o.srcset);
      else img.removeAttribute("srcset");
      if (o.src != null) img.setAttribute("src", o.src);
    }
    var hoverImg = thumb.querySelector(".hover-file-image");
    if (hoverImg) hoverImg.style.backgroundImage = o.hoverBg || "";
    var hoverVid = thumb.querySelector(".hover-file-video");
    if (hoverVid) hoverVid.style.display = o.hoverVidDisplay || ""; // un-hide
    // Drop the L-view carousel colorway overlay (pristine = no overlay).
    var carOverlay = thumb.querySelector(".thumb-carousel-swatch-overlay");
    if (carOverlay) carOverlay.remove();
    // Reset the swatch selection + add-to-cart variant to the original.
    thumb.querySelectorAll(".variant-swatch").forEach(function (s) {
      s.classList.remove("active");
    });
    if (o.activeSwatch) {
      o.activeSwatch.classList.add("active");
      // Restores variant id, href and buy state together.
      applyCardVariantState(thumb, o.activeSwatch);
    } else if (o.variantId) {
      thumb.setAttribute("data-variant-id", o.variantId);
    }
    cardOriginals.delete(thumb); // pristine again — a later swap re-captures
  }

  // Reflect one colorway on the card: which variant add-to-cart uses, where
  // the card links, and whether that colorway can be bought at all.
  //
  // product.available is only false when EVERY variant is out, so a part
  // sold-out product keeps an actionable quick-add while individual colorways
  // are not buyable. Selecting a sold-out swatch flips the quick-add to
  // .pe-none — the existing "not actionable" marker — so the click falls
  // through to the PDP in waitlist mode instead of posting an unbuyable
  // variant to /cart/add.js.
  //
  // Shared by swatch selection and revertCard, so the reverted card can't be
  // left showing another colorway's buy state.
  function applyCardVariantState(thumb, swatch) {
    if (!thumb || !swatch) return;
    var vid = swatch.getAttribute("data-variant-id");
    var avail = swatch.getAttribute("data-variant-available") !== "false";
    if (vid) thumb.setAttribute("data-variant-id", vid);

    var qaWrap = thumb.querySelector(".quick-add-variants");
    if (qaWrap) {
      qaWrap.classList.toggle("pe-none", !avail);
      // Only the desktop label changes — .mobile stays "+", and the two spans
      // have to survive intact (setting textContent on the button itself would
      // flatten them and break the responsive hide rules).
      var label = qaWrap.querySelector(".quick-add-button .desktop");
      if (label) label.textContent = avail ? "ADD TO CART" : "JOIN WAITLIST";
    }

    // Keep the card link on the selected colorway. Without this, picking a
    // swatch and then clicking the card opened the FIRST variant's PDP.
    var base = thumb.getAttribute("data-product-url");
    if (base && vid) {
      thumb.setAttribute(
        "href",
        base +
          (base.indexOf("?") > -1 ? "&" : "?") +
          "variant=" +
          vid +
          (avail ? "" : "&waitlist"),
      );
    }
  }

  function clearRevert(thumb) {
    var t = revertTimers.get(thumb);
    if (t) {
      clearTimeout(t);
      revertTimers.delete(thumb);
    }
  }

  function scheduleRevert(thumb) {
    clearRevert(thumb);
    if (!cardOriginals.has(thumb)) return; // never swapped → nothing to revert
    revertTimers.set(
      thumb,
      setTimeout(function () {
        revertTimers.delete(thumb);
        revertCard(thumb);
      }, REVERT_DELAY),
    );
  }

  // Cancel a pending revert whenever the pointer is over / interacting with a card.
  document.addEventListener("pointerover", function (e) {
    var thumb = e.target.closest && e.target.closest(".shop-thumb");
    if (thumb) clearRevert(thumb);
  });
  // Start the 3s countdown when the pointer leaves the card (desktop) or a tap
  // lifts (touch). Ignore transitions that stay within the same card.
  document.addEventListener("pointerout", function (e) {
    var thumb = e.target.closest && e.target.closest(".shop-thumb");
    if (!thumb) return;
    var to = e.relatedTarget;
    if (to && thumb.contains(to)) return; // moved within the card, not leaving
    scheduleRevert(thumb);
  });

  // ── PLP swatch image prefetch ────────────────────────────────────────────
  // The swatch swap (below) sets img.src to the swatch's data-variant-image on
  // click — which otherwise isn't fetched until that moment, so the swapped
  // colorway visibly loads in. Warm those exact URLs into the HTTP cache the
  // instant the user shows intent to use the swatches: hovering the swatch
  // quick-add area (desktop) or tapping the "+" (mobile). The prefetched URL is
  // identical to the one the swap assigns, so the later src set is a cache hit.
  // Once per card; scoped to variant cards (only swatches carry the attribute).
  var prefetchedCards = new WeakSet();
  function prefetchCardVariantImages(thumb) {
    if (!thumb || prefetchedCards.has(thumb)) return;
    var swatches = thumb.querySelectorAll(
      ".variant-swatch[data-variant-image]",
    );
    if (!swatches.length) return;
    prefetchedCards.add(thumb);
    swatches.forEach(function (s) {
      var url = s.getAttribute("data-variant-image");
      if (!url) return;
      var pre = new Image();
      pre.decoding = "async";
      pre.src = url;
    });
  }
  // Desktop: pointer entered the swatch quick-add area (revealed on card hover,
  // so this is a strong "about to pick a color" signal). Mouse only — touch
  // goes through the "+" tap path instead.
  document.addEventListener(
    "pointerover",
    function (e) {
      if (e.pointerType && e.pointerType !== "mouse") return;
      if (!e.target.closest) return;
      if (e.target.closest(".quick-add-variants")) {
        prefetchCardVariantImages(e.target.closest(".shop-thumb"));
      }
    },
    { passive: true },
  );

  // Re-render a cents amount in the same shape as an existing money string
  // (`like`), so the qty-multiplied total keeps the market's currency symbol
  // and placement — e.g. "$192.43" → "$962.15", "CA$1,234.56 CAD" style intact.
  // Mirrors Shopify's money_without_trailing_zeros: thousands separators, and
  // ".00" dropped on round amounts.
  function formatMoneyLike(cents, like) {
    var s = String(like);
    var m = s.match(/[\d.,]+/);
    var prefix = m ? s.slice(0, m.index) : "$";
    var suffix = m ? s.slice(m.index + m[0].length) : "";
    var whole = Math.floor(cents / 100);
    var frac = cents % 100;
    var wholeStr = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    var num =
      frac === 0 ? wholeStr : wholeStr + "." + (frac < 10 ? "0" : "") + frac;
    return prefix + num + suffix;
  }

  // ── Click delegation ─────────────────────────────────────────────────────
  document.addEventListener("click", function (e) {
    // Open cart
    if (e.target.closest("#header-cart-trigger")) {
      e.preventDefault();
      openDrawer();
      return;
    }

    // Close cart
    if (e.target.closest("#cart .nav-x")) {
      e.preventDefault();
      closeDrawer();
      return;
    }

    // Cart drawer quantity controls
    var qCtrl = e.target.closest("#cart .q-control");
    if (qCtrl) {
      e.preventDefault();
      if (inflight) return;
      var line = qCtrl.closest(".cart-item");
      if (!line) return;
      var vid = line.getAttribute("cart-item-variant-id");
      var current =
        parseInt(line.querySelector(".q-value").textContent, 10) || 0;
      var minus = qCtrl.classList.contains("q-control-minus");
      var next = Math.max(0, current + (minus ? -1 : 1));
      changeItem(vid, next);
      return;
    }

    // Per-line remove
    var removeBtn = e.target.closest(".cart-item-remove");
    if (removeBtn) {
      e.preventDefault();
      var vidR = removeBtn.getAttribute("data-variant-id");
      if (vidR) {
        // Optimistic: collapse the row out of layout immediately so the
        // removal feels instant, instead of waiting on the server
        // round-trip + cart re-render. The authoritative HTML (correct
        // totals + shipping bar) swaps in when changeItem resolves. Only
        // do this when no other cart op is in flight — otherwise the
        // change would be dropped by changeItem's inflight guard and the
        // row would reappear on the next render.
        if (!inflight) {
          var row = removeBtn.closest(".cart-item");
          if (row) row.style.display = "none";
        }
        changeItem(vidR, 0);
      }
      return;
    }

    // Variant swatch selection inside a product card (color-only quick-add)
    var swatch = e.target.closest(".variant-swatch");
    if (swatch) {
      e.preventDefault();
      e.stopPropagation();
      var swThumb = swatch.closest(".shop-thumb");
      if (!swThumb) return;
      // Snapshot the pristine card state before the first swap, and cancel any
      // pending revert — this is an active interaction (see revert system above).
      captureCardOriginal(swThumb);
      clearRevert(swThumb);
      swThumb.querySelectorAll(".variant-swatch").forEach(function (s) {
        s.classList.remove("active");
      });
      swatch.classList.add("active");
      var newVid = swatch.getAttribute("data-variant-id");
      // Variant id + card href + buy state for this colorway.
      applyCardVariantState(swThumb, swatch);
      // Swap the card image to this color's photo. The swatch carries an
      // 800w 3:4 URL (shop-thumb.liquid); drop srcset so the browser honors
      // the new src rather than re-picking the original responsive candidate.
      var newImg = swatch.getAttribute("data-variant-image");
      if (newImg) {
        var thumbImg = swThumb.querySelector("img.thumb-image-img");
        if (thumbImg && thumbImg.src !== newImg) {
          thumbImg.removeAttribute("srcset");
          thumbImg.src = newImg;
        }
        // On desktop the hover layer sits on top of the main image (and video
        // hovers show whenever the card is in view), so the user is looking at
        // the hover layer while hovering to pick a swatch — swapping only the
        // main image would be invisible. Reflect the color there too: image
        // hovers get their background swapped; video hovers are hidden so the
        // swapped main image shows through.
        var hoverImg = swThumb.querySelector(".hover-file-image");
        if (hoverImg) hoverImg.style.backgroundImage = "url('" + newImg + "')";
        var hoverVid = swThumb.querySelector(".hover-file-video");
        if (hoverVid) hoverVid.style.display = "none";
        // In the L view the card carousel overlays the base thumbnail, which
        // would hide the swap. Show the colorway image as an overlay ON TOP of
        // the carousel (never mutating the slides — tagged editorials stay
        // intact). Cleared by arrow navigation and by the idle revert.
        var swCarousel = swThumb.querySelector(".thumb-carousel");
        if (swCarousel) {
          var swOverlay = swCarousel.querySelector(
            ".thumb-carousel-swatch-overlay",
          );
          if (!swOverlay) {
            swOverlay = document.createElement("img");
            swOverlay.className = "thumb-carousel-swatch-overlay";
            swOverlay.alt = "";
            swCarousel.appendChild(swOverlay);
          }
          swOverlay.src = newImg;
        }
      }
      return;
    }

    // Mobile quick-add toggle (the "+"/"×" box on a card). Touch has no hover,
    // so tapping it reveals the quick-add UI by flipping .qa-open on the card
    // (CSS rotates the "+" to "×" and shows the quick-add bar).
    var qaToggle = e.target.closest(".thumb-qa-toggle");
    if (qaToggle) {
      e.preventDefault();
      e.stopPropagation();
      var qaThumb = qaToggle.closest(".shop-thumb");
      if (qaThumb) {
        var qaOpened = qaThumb.classList.toggle("qa-open");
        // Opening the quick-add is the mobile intent signal — warm the swatch
        // images now so the color swap is instant when they tap one.
        if (qaOpened) prefetchCardVariantImages(qaThumb);
      }
      return;
    }

    // Quick-add (product grid)
    var quickAdd = e.target.closest(".quick-add");
    if (quickAdd && !quickAdd.classList.contains("pe-none")) {
      // For the variants version, only the ADD TO CART button fires the add —
      // empty space inside .variant-swatches should be a no-op.
      var isVariantQuickAdd = quickAdd.classList.contains("quick-add-variants");
      if (isVariantQuickAdd && !e.target.closest(".quick-add-button")) {
        return;
      }
      e.preventDefault();
      var thumb = quickAdd.closest(".shop-thumb");
      var qvid = thumb && thumb.getAttribute("data-variant-id");
      if (!qvid) return;
      var mobile = window.innerWidth < 768;
      // Only rewrite the label, not the whole quick-add (would wipe swatches)
      var labelEl = isVariantQuickAdd
        ? quickAdd.querySelector(".quick-add-button")
        : quickAdd;
      // Snapshot the inner HTML so the responsive .desktop / .mobile spans
      // are restored intact — textContent flattens them into one string and
      // breaks the responsive hide rules.
      var origHTML = labelEl.innerHTML;
      labelEl.textContent = mobile ? "..." : "ADDING...";
      quickAdd.classList.add("adding");
      addItem(qvid, 1).then(function () {
        labelEl.textContent = mobile ? "✓" : "ADDED";
        setTimeout(function () {
          labelEl.innerHTML = origHTML;
          quickAdd.classList.remove("adding");
        }, 2000);
      });
      return;
    }

    // PDP main add-to-cart click → handled via the form submit branch
    // below for the standard (form-wrapped) flow. Inquire mode's ATC is
    // a bare <a> mailto link outside the form — let the browser follow
    // it. No-op here so we don't double-handle.

    // "Email When Available" — surfaces the Klaviyo BIS modal for the
    // currently selected (sold-out) variant. The Klaviyo widget loads
    // its own trigger as .BIS_trigger; clicking that opens the form
    // pre-bound to the right variant id (klaviyo reads it from the
    // page). Legacy project.js bound the same flow on
    // .the-product-form #BIS_trigger_button — we wire the new markup
    // here.
    var bisBtn = e.target.closest("#BIS_trigger_button");
    if (bisBtn) {
      e.preventDefault();
      var trigger = document.querySelector(".BIS_trigger");
      if (trigger) trigger.click();
      return;
    }

    // PDP qty +/- controls — clamp to [1, inventory] where inventory
    // comes from applyCartState's data-current-inventory cache on
    // #product-form. Updates the live #product-price total so the
    // shopper sees qty × unit price update in place.
    var qBtn = e.target.closest(".pdp-qty-row .q-control");
    if (qBtn) {
      var qValueEl = qBtn.closest(".item-quantity").querySelector(".q-value");
      if (!qValueEl) return;
      var minus = qBtn.classList.contains("q-control-minus");
      // .q-value is now an <input type="number"> on the PDP (form
      // best-practice). Other locations may still use a <span> (cart
      // drawer line items) — fall through to .textContent there.
      var isInput = qValueEl.tagName === "INPUT";
      var current =
        parseInt(isInput ? qValueEl.value : qValueEl.textContent, 10) || 1;
      var form = document.getElementById("product-form");
      var inventoryRaw = form && form.getAttribute("data-current-inventory");
      var inventory = inventoryRaw ? parseInt(inventoryRaw, 10) : Infinity;
      // Inventory of 0 (sold-out variant) — qty stays at 1 either way;
      // BIS button handles the user flow there.
      if (!isFinite(inventory) || inventory < 1) inventory = Infinity;
      var next = Math.max(1, Math.min(inventory, current + (minus ? -1 : 1)));
      if (next === current) return;
      if (isInput) {
        qValueEl.value = String(next);
        // Fire 'input' so any other listener (validation, etc.) sees it.
        qValueEl.dispatchEvent(new Event("input", { bubbles: true }));
      } else {
        qValueEl.textContent = String(next);
      }
      // Multiply the unit price display by the new qty. Do the maths in whole
      // CENTS — multiplying the parsed float directly produces artefacts like
      // 192.43 * 5 = 962.1500000000001 (very visible once a market's currency
      // conversion makes the unit price non-round). Math.round(unit * 100)
      // recovers the exact cents, and integer * integer stays exact.
      var priceEl = document.getElementById("product-price");
      var og = priceEl && priceEl.getAttribute("data-og-price");
      if (priceEl && og) {
        var unit = parseFloat(String(og).replace(/[^0-9.]/g, ""));
        if (!isNaN(unit)) {
          var totalCents = Math.round(unit * 100) * next;
          var priceSpan = priceEl.querySelector(".price");
          var compareSpan = priceEl.querySelector(".compare-at");
          if (priceSpan && compareSpan) {
            // On-sale variant: update the two spans in place rather than
            // replacing the element's contents, which wiped the strikethrough.
            priceSpan.textContent = formatMoneyLike(totalCents, og);
            var ogCompare = priceEl.getAttribute("data-og-compare-at");
            var cUnit = ogCompare
              ? parseFloat(String(ogCompare).replace(/[^0-9.]/g, ""))
              : NaN;
            if (!isNaN(cUnit)) {
              // Scale the "was" price too, so both reflect the same quantity.
              var cTotal = Math.round(cUnit * 100) * next;
              // Rebuild keeping the .bar element (its text sits beside it).
              compareSpan.innerHTML = '<span class="bar"></span>';
              compareSpan.appendChild(
                document.createTextNode(formatMoneyLike(cTotal, ogCompare)),
              );
            }
          } else {
            priceEl.textContent = formatMoneyLike(totalCents, og);
          }
        }
      }
      return;
    }

    // Cart upsell row — whole card adds to cart (renderRecommendations output)
    var upItem = e.target.closest("#cart-upsells .cart-upsell-item");
    if (upItem) {
      e.preventDefault();
      addUpsellFromItem(upItem);
      return;
    }

    // Fallback: click outside the drawer while it's open → close it.
    // All specific cart triggers above have returned by now, so anything
    // landing here is unrelated to the cart's own controls.
    var openCart = document.querySelector(CART_SEL);
    if (
      openCart &&
      openCart.classList.contains("nav-open") &&
      !e.target.closest("#cart") &&
      // The paired Klaviyo flyout sits OUTSIDE #cart, so without this a click
      // into the email field would count as an outside click and close the
      // drawer (which then dismisses the flyout being typed into).
      !e.target.closest("." + KL_TAG)
    ) {
      closeDrawer();
    }
  });

  document.addEventListener("keydown", function (e) {
    if (e.key !== "Enter" && e.key !== " ") return;
    var upItem = e.target.closest("#cart-upsells .cart-upsell-item");
    if (!upItem) return;
    e.preventDefault();
    addUpsellFromItem(upItem);
  });

  // PDP product form submit — progressively enhances the native
  // /cart/add POST into our /cart/add.js + drawer flow. Form-disabled
  // fallback (no JS) still posts to /cart/add the standard way.
  //
  // The submit event fires for:
  //   - clicking #add-to-cart (type="submit")
  //   - pressing Enter while focused inside the form
  //   - {{ form | payment_button }} → handled by Shopify natively; we
  //     don't preventDefault because the dynamic-checkout flow needs
  //     to do its own POST. We detect the e.submitter and bail out.
  document.addEventListener("submit", function (e) {
    var form = e.target;
    // NB: form.id is shadowed by the form's <input name="id"> (variant id), so
    // it returns that element, not the id attribute — read the attribute directly.
    if (!form || form.getAttribute("id") !== "product-form-cart") return;

    // The Shop Pay / dynamic-checkout button is rendered by Shopify
    // inside the form and has name="checkout" + formaction = its own
    // endpoint. Don't intercept those.
    var submitter = e.submitter;
    if (submitter && submitter !== document.getElementById("add-to-cart")) {
      return;
    }

    var atc = document.getElementById("add-to-cart");
    if (
      atc &&
      (atc.classList.contains("soldout") || atc.classList.contains("hidden"))
    ) {
      // Defensive: shouldn't happen because the sold-out ATC is
      // display:none, but block submit if it does.
      e.preventDefault();
      return;
    }

    e.preventDefault();

    var variantIdInput = form.querySelector('input[name="id"]');
    var qtyInput = form.querySelector('input[name="quantity"]');
    var pvid = variantIdInput ? variantIdInput.value : null;
    if (!pvid) return;
    var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
    var preorder = atc && atc.getAttribute("data-preorder");
    var props = preorder ? { Preorder: preorder } : null;

    // Loading state — visual feedback during the round-trip. The
    // .is-loading class swaps the label for a spinner; addItem's
    // inflight guard prevents duplicate submissions even without this.
    if (atc) atc.classList.add("is-loading");
    addItem(pvid, qty, props).then(function () {
      if (atc) atc.classList.remove("is-loading");
    });
  });

  // Expose for legacy interop (e.g. preorder flow, third-party widgets)
  window.SLJCart = {
    addItem: addItem,
    changeItem: changeItem,
    open: openDrawer,
    close: closeDrawer,
  };
  // Back-compat shims so any straggling legacy calls keep working
  window.addToCart = function (id, quantity, properties) {
    return addItem(id, quantity, properties);
  };
  window.updateQ = function (id, count) {
    return changeItem(id, count);
  };
})();

// ─── Input modality: keyboard vs pointer ──────────────────────────────────
// Show focus rings only for keyboard users. Safari applies :focus-visible to
// mouse-clicked AND programmatically .focus()'d elements, so the PDP variant
// options drew a heavy black focus box on selection (the picker focuses the
// option when its panel opens). We gate focus rings on an explicit keyboard
// signal (html.using-keyboard) instead of :focus-visible — see _pdp.scss.
(function () {
  var root = document.documentElement;
  var navKeys = {
    Tab: 1,
    ArrowUp: 1,
    ArrowDown: 1,
    ArrowLeft: 1,
    ArrowRight: 1,
    Home: 1,
    End: 1,
  };
  document.addEventListener(
    "keydown",
    function (e) {
      if (navKeys[e.key]) root.classList.add("using-keyboard");
    },
    true,
  );
  document.addEventListener(
    "pointerdown",
    function () {
      root.classList.remove("using-keyboard");
    },
    true,
  );
})();

// ─── PDP variant picker ───────────────────────────────────────────────────
// Vanilla custom dropdown that replaces the legacy customSelect jQuery
// plugin. Reads variant data from window.PDP_PRODUCT (rendered by
// sections/main-product.liquid) and builds one .pdp-picker per product
// option. Works for color / size / material — any option type. Color
// swatches fill if the option value parses as a CSS color, otherwise the
// dot is just an outlined ring.

(function () {
  function init() {
    // Observers are independent of variant-picker presence — start
    // them first so single-variant products (which don't render
    // #variant-pickers) still get thumb-scroll tracking and video
    // autoplay. They no-op if there's nothing matching the selector.
    initThumbObserver();
    initPdpVideoObserver();
    initPdpCarousel();

    // GROUPED gallery mode: the stack is server-rendered in color-grouped
    // order but the thumb strip renders in media order — mirror it once.
    if (
      document.querySelector(".product-images .pdp-image[data-color-group]")
    ) {
      syncPdpThumbOrder();
    }

    var data = window.PDP_PRODUCT;
    var container = document.getElementById("variant-pickers");
    if (!data || !data.options) {
      // No PDP_PRODUCT data — fall back to the ATC button's
      // data-variant attribute to seed applyCartState.
      var btn = document.getElementById("add-to-cart");
      if (btn) applyCartState(btn.getAttribute("data-variant"));
      return;
    }

    // Current selection from URL ?variant= or first available variant.
    var url = new URL(window.location.href);
    var currentVariantId = parseInt(url.searchParams.get("variant"), 10);
    var currentVariant =
      data.variants.find(function (v) {
        return v.id === currentVariantId;
      }) ||
      data.variants.find(function (v) {
        return v.available;
      }) ||
      data.variants[0];
    if (!currentVariant) return;

    // Pickers are server-rendered in sections/main-product.liquid; only
    // build client-side as a fallback if the container is missing the
    // expected markup. Container itself is absent on single-variant
    // products (totalVariants > 1 gate in Liquid) — that's expected,
    // skip the rebuild and continue with applyCartState below.
    if (container && !container.querySelector(".pdp-picker")) {
      buildPickersClientSide(container, data, currentVariant);
    }

    // Initial image filter is pre-applied in Liquid; calling it again
    // here is a no-op for matching variants and would briefly thrash
    // is-filtered-out for non-matching ones, so skip.
    applyCartState(currentVariant.id);
  }

  // Client-side fallback picker builder — only used if the
  // server-rendered markup is somehow missing. Same shape as the
  // Liquid template so click/keyboard handlers find the elements they
  // expect.
  function buildPickersClientSide(container, data, currentVariant) {
    container.innerHTML = "";

    data.options.forEach(function (opt, optIndex) {
      // Stable id prefix for ARIA wiring (label ↔ trigger ↔ listbox ↔ options).
      var idPrefix = "pdp-picker-" + optIndex;
      var labelId = idPrefix + "-label";
      var triggerId = idPrefix + "-trigger";
      var panelId = idPrefix + "-panel";

      var picker = document.createElement("div");
      picker.className = "pdp-picker";
      picker.dataset.optionIndex = String(optIndex);
      picker.dataset.optionName = opt.name.toLowerCase();

      // Heading — visible label above the trigger. <label> is the right
      // element here even though the trigger is a button (not an input);
      // the label is also referenced via aria-labelledby below so SR
      // users hear "Select Base, button, collapsed, …".
      var heading = document.createElement("label");
      heading.className = "pdp-picker-label";
      heading.id = labelId;
      heading.htmlFor = triggerId;
      heading.textContent = "Select " + opt.name;

      // Trigger — combobox-style disclosure button. aria-haspopup +
      // aria-controls + aria-expanded follow the WAI-ARIA listbox-
      // popup pattern.
      var trigger = document.createElement("button");
      trigger.type = "button";
      trigger.id = triggerId;
      trigger.className = "pdp-picker-trigger";
      trigger.setAttribute("aria-haspopup", "listbox");
      trigger.setAttribute("aria-controls", panelId);
      trigger.setAttribute("aria-expanded", "false");
      trigger.setAttribute("aria-labelledby", labelId + " " + triggerId);
      trigger.innerHTML =
        swatchSpanHtml(opt, currentVariant.options[optIndex]) +
        '<span class="pdp-picker-value">' +
        escapeHtml(currentVariant.options[optIndex]) +
        "</span>" +
        '<span class="pdp-picker-chevron" aria-hidden="true"></span>';

      // Panel — listbox with one option per value.
      var panel = document.createElement("ul");
      panel.className = "pdp-picker-panel";
      panel.id = panelId;
      panel.hidden = true;
      panel.setAttribute("role", "listbox");
      panel.setAttribute("aria-labelledby", labelId);

      opt.values.forEach(function (val, valIdx) {
        var li = document.createElement("li");
        var optionId = idPrefix + "-option-" + valIdx;
        li.id = optionId;
        li.className = "pdp-picker-option";
        li.setAttribute("role", "option");
        li.dataset.value = val;
        // Roving tabindex pattern: the selected option is the only one
        // focusable when the panel is closed; on open we move focus to
        // it (or to the first option if none is selected).
        var isSelected = val === currentVariant.options[optIndex];
        if (isSelected) {
          li.setAttribute("aria-selected", "true");
          li.tabIndex = 0;
        } else {
          li.tabIndex = -1;
        }
        li.innerHTML =
          swatchSpanHtml(opt, val) +
          '<span class="pdp-picker-value">' +
          escapeHtml(val) +
          "</span>";
        panel.appendChild(li);
      });

      picker.appendChild(heading);
      picker.appendChild(trigger);
      picker.appendChild(panel);
      container.appendChild(picker);
    });
  }

  // Look up the variant matching the currently selected option values across
  // all pickers, then update URL, add-to-cart data-variant, price, and call
  // the legacy switchVariant() (for image swap, availability flags, etc.).
  function variantChange() {
    var data = window.PDP_PRODUCT;
    if (!data) return;

    var pickers = document.querySelectorAll(".pdp-picker");
    if (!pickers.length) return;

    var selectedOptions = Array.prototype.map.call(pickers, function (p) {
      var value = p.querySelector(".pdp-picker-trigger .pdp-picker-value");
      return value ? value.textContent : "";
    });

    var variant = data.variants.find(function (v) {
      return v.options.every(function (o, i) {
        return o === selectedOptions[i];
      });
    });
    if (!variant) return;

    // URL
    var url = new URL(window.location.href);
    url.searchParams.set("variant", variant.id);
    window.history.replaceState({}, "", url);

    // Cart state — label, hidden, sold-out, BIS toggle, preorder, price.
    applyCartState(variant.id);

    // Gallery update. GROUPED mode (alt-tag color groups present on the
    // stack): reorder everything — selected color's group first, lead image
    // leading, other groups in picker order, ungrouped last. LEGACY mode
    // (untagged product): filter to the variant's attached images
    // (option1-keyed) exactly as before.
    if (
      document.querySelector(".product-images .pdp-image[data-color-group]")
    ) {
      var colorIdx = data.colorOptionIndex >= 0 ? data.colorOptionIndex : 0;
      regroupImagesByColor(selectedOptions[colorIdx], variant.id);
    } else {
      filterImagesByOption1(selectedOptions[0], variant.id);
    }

    // NOTE: legacy window.switchVariant() (project.js) used to be called
    // here as a fallback. It manipulated #add-to-cart label / hidden /
    // soldout / #BIS_trigger_button / #product-price — every one of
    // which is now owned by applyCartState above, so calling it would
    // overwrite our state. The old function also drove a Flickity image
    // carousel that the new PDP doesn't use. Don't call it.
  }

  // Apply the full ADD TO CART region state to a given variant id.
  //
  // The Liquid template renders availability data on #product-form via
  // snippets/variant-availability.liquid:
  //   data-variant-{id}-available  → inventory_quantity (only present
  //                                   when the variant is in stock)
  //   data-variant-{id}-price      → formatted price ("$60")
  //   data-variant-{id}-compare-at → formatted compare-at price, present
  //                                   only when the variant has one
  //   data-variant-{id}-preorder   → preorder ship date, present only
  //                                   when the variant is on preorder
  //
  // Three buttons live in .pdp-cta-row; this function flips exactly one
  // visible (or, in inquire mode, leaves the inquire link alone):
  //   - #add-to-cart            → "Add to Cart" / "Pre-Order"
  //   - #add-to-cart.soldout    → "SOLD OUT" (kept .hidden by legacy
  //                               flow; we keep it that way and surface
  //                               the BIS button instead)
  //   - #BIS_trigger_button     → "Email When Available"
  function applyCartState(variantId) {
    var form = document.getElementById("product-form");
    var btn = document.getElementById("add-to-cart");
    var bis = document.getElementById("BIS_trigger_button");
    if (!form || !btn) return;

    btn.setAttribute("data-variant", variantId);

    // Keep the hidden variant input in sync so the native form POST
    // (or our /cart/add.js intercept) always sends the right id.
    var hiddenId = document.getElementById("product-form-variant-id");
    if (hiddenId) hiddenId.value = String(variantId);

    // Inquire-mode link doesn't have a sold-out state — it's a mailto.
    if (btn.classList.contains("inquire")) return;

    var availableAttr = "data-variant-" + variantId + "-available";
    var compareAtAttr = "data-variant-" + variantId + "-compare-at";
    var priceAttr = "data-variant-" + variantId + "-price";
    var preorderAttr = "data-variant-" + variantId + "-preorder";

    var isAvailable = form.hasAttribute(availableAttr);
    // The attribute's PRESENCE means available; its value is the purchasable
    // ceiling. Blank (untracked / oversell variant — see
    // snippets/variant-availability.liquid) parses to NaN and falls through to
    // Infinity, i.e. no cap.
    var inventory = isAvailable
      ? parseInt(form.getAttribute(availableAttr), 10) || Infinity
      : 0;
    var preorder = form.getAttribute(preorderAttr);
    var price = form.getAttribute(priceAttr);
    var compareAt = form.getAttribute(compareAtAttr);

    btn.classList.toggle("available", isAvailable);
    btn.classList.toggle("soldout", !isAvailable);

    if (isAvailable) {
      // In stock: show ATC, hide BIS, set the right label.
      if (preorder) {
        btn.setAttribute("data-preorder", preorder);
        btn.textContent = "Pre-Order";
      } else {
        btn.removeAttribute("data-preorder");
        btn.textContent = "Add to Cart";
      }
      btn.classList.remove("hidden");
      if (bis) bis.classList.add("hidden");
    } else {
      // Sold out: hide ATC, surface BIS. Clearing data-preorder so a
      // returning user (variant flip) doesn't keep the stale flag.
      btn.removeAttribute("data-preorder");
      btn.textContent = "SOLD OUT";
      btn.classList.add("hidden");
      if (bis) bis.classList.remove("hidden");
    }

    // Stash inventory ceiling so the qty +/- handler can cap at it.
    form.setAttribute("data-current-inventory", inventory);

    // Price — variant-availability snippet always emits a price attr.
    // If a compare-at is present we render strikethrough + sale price,
    // matching the .thumb-price-varies treatment on PLP cards.
    var priceEl = document.getElementById("product-price");
    if (priceEl && price) {
      if (compareAt) {
        priceEl.innerHTML =
          '<span class="compare-at"><span class="bar"></span>' +
          escapeHtml(compareAt) +
          '</span> <span class="price">' +
          escapeHtml(price) +
          "</span>";
        priceEl.classList.add("product-price-varies");
      } else {
        priceEl.textContent = price;
        priceEl.classList.remove("product-price-varies");
      }
      // Reset og-price so the qty multiplier reads the unit price. Same for the
      // compare-at, so the qty handler can scale BOTH and keep the
      // strikethrough intact (it used to overwrite the whole element).
      priceEl.setAttribute("data-og-price", price);
      if (compareAt) {
        priceEl.setAttribute("data-og-compare-at", compareAt);
      } else {
        priceEl.removeAttribute("data-og-compare-at");
      }
    }

    // Variant switched while qty > inventory — reset to 1 so we don't
    // submit a value the server will reject. Update max= so the native
    // input UI (spinner / step) also respects the new ceiling.
    var qtyEl = document.querySelector(".pdp-qty-row .q-value");
    if (qtyEl) {
      var isInput = qtyEl.tagName === "INPUT";
      var current =
        parseInt(isInput ? qtyEl.value : qtyEl.textContent, 10) || 1;
      if (current > inventory) {
        if (isInput) qtyEl.value = "1";
        else qtyEl.textContent = "1";
      }
      if (isInput) {
        // Drop max= entirely for an unlimited variant — otherwise flipping
        // from a capped colorway to an oversell one would leave the old
        // ceiling behind and keep the + button stuck.
        if (isFinite(inventory)) qtyEl.setAttribute("max", String(inventory));
        else qtyEl.removeAttribute("max");
      }
    }
  }

  // Show only images attached to a variant whose option1 matches `value`,
  // plus all universal (non-variant-attached) images. If nothing matches at
  // all, fall back to showing every image so the stack never goes blank.
  // When `variantId` is provided, hoist that variant's attached image to
  // the top of the stack so the currently-selected variant leads.
  // Also mirrors the same filter onto the thumbnail strip (.test-slideshow
  // .mini-pic) — each thumb carries the same data-option1 / data-universal
  // as its source image, so we just apply the same toggle.
  function filterImagesByOption1(value, variantId) {
    var container = document.querySelector(".product-images");
    if (!container) return;
    var images = container.querySelectorAll(".pdp-image");
    if (!images.length) return;

    var anyMatched = false;

    images.forEach(function (img) {
      if (img.hasAttribute("data-universal")) {
        img.classList.remove("is-filtered-out");
        anyMatched = true;
        return;
      }
      var matches = img.getAttribute("data-option1") === value;
      img.classList.toggle("is-filtered-out", !matches);
      if (matches) anyMatched = true;
    });

    if (!anyMatched) {
      // No variant-attached image for this color — show everything so the
      // stack isn't blank.
      images.forEach(function (img) {
        img.classList.remove("is-filtered-out");
      });
    }

    // Hoist the selected variant's attached image to the top of the stack,
    // and mirror the same hoist on the thumbnail strip so the variant
    // thumb leads alongside its main-stack image.
    var featuredIdx = null;
    if (variantId != null) {
      var featured = container.querySelector(
        '.pdp-image[data-variant-image="' + variantId + '"]',
      );
      if (featured) {
        featuredIdx = featured.getAttribute("data-image-index");
        if (container.firstChild !== featured) {
          container.insertBefore(featured, container.firstChild);
        }
      }
    }

    // Mirror filter state onto thumbnail strip via data-image-index pairing.
    var strip = document.querySelector(".test-slideshow");
    var thumbs = strip ? strip.querySelectorAll(".mini-pic") : [];
    thumbs.forEach(function (thumb) {
      var idx = thumb.getAttribute("data-image-index");
      var img = container.querySelector(
        '.pdp-image[data-image-index="' + idx + '"]',
      );
      if (!img) return;
      thumb.classList.toggle(
        "is-filtered-out",
        img.classList.contains("is-filtered-out"),
      );
    });

    // Hoist the matching thumb to the front of the strip.
    if (strip && featuredIdx != null) {
      var featuredThumb = strip.querySelector(
        '.mini-pic[data-image-index="' + featuredIdx + '"]',
      );
      if (featuredThumb && strip.firstChild !== featuredThumb) {
        strip.insertBefore(featuredThumb, strip.firstChild);
      }
    }

    // Reset the mobile carousel to the first (now-featured) slide + refresh
    // the counter for the filtered set.
    refreshPdpCarousel();
  }

  // ── Color grouping (alt-tag system) ────────────────────────────────────
  // GROUPED-mode counterpart to filterImagesByOption1. The Liquid render
  // (main-product.liquid) classifies each image from its alt segments
  // ("Product Name - Color[ - Tag]") into data-color-group and
  // server-renders the initial order; this redoes that ordering on variant
  // change: selected color's group first, then the other groups in picker
  // order, then ungrouped. Each group leads with its color's VARIANT image
  // (the variant image slot — "Product Image" tags are reserved for the
  // storefront cards and play no part here). Editorial images never render
  // in the stack, so there's nothing to hide — all nodes stay visible, only
  // order changes (appendChild moves nodes, never refetches).

  function pdpHandleize(str) {
    return String(str || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  // Mirror the stack's current order onto the thumbnail strip (pairing by
  // data-image-index). Also used once on load in grouped mode — thumbs are
  // server-rendered in media order.
  function syncPdpThumbOrder() {
    var container = document.querySelector(".product-images");
    var strip = document.querySelector(".test-slideshow");
    if (!container || !strip) return;
    container.querySelectorAll(".pdp-image").forEach(function (node) {
      var idx = node.getAttribute("data-image-index");
      var t = strip.querySelector('.mini-pic[data-image-index="' + idx + '"]');
      if (t) strip.appendChild(t);
    });
  }

  function regroupImagesByColor(colorValue, variantId) {
    var container = document.querySelector(".product-images");
    if (!container) return;
    var imgs = Array.prototype.slice.call(
      container.querySelectorAll(".pdp-image"),
    );
    if (!imgs.length) return;

    var selHandle = pdpHandleize(colorValue);
    var pdpData = window.PDP_PRODUCT || {};
    var order = (pdpData.colorOrder || []).map(pdpHandleize);

    // color handle -> [variant ids] map, so each group can hoist its
    // color's variant image to the front.
    var colorVariantIds = {};
    if (pdpData.colorOptionIndex >= 0 && pdpData.variants) {
      pdpData.variants.forEach(function (v) {
        var h = pdpHandleize(v.options[pdpData.colorOptionIndex]);
        (colorVariantIds[h] = colorVariantIds[h] || []).push(String(v.id));
      });
    }

    // Canonical order = original media order, independent of current DOM.
    imgs.sort(function (a, b) {
      return (
        parseInt(a.getAttribute("data-image-index"), 10) -
        parseInt(b.getAttribute("data-image-index"), 10)
      );
    });

    var buckets = {};
    var ungrouped = [];
    imgs.forEach(function (node) {
      var g = node.getAttribute("data-color-group") || "";
      if (g) (buckets[g] = buckets[g] || []).push(node);
      else ungrouped.push(node);
    });

    // Lead = the group's variant image. For the selected group, prefer the
    // exact selected variant's image, then any variant of that color; for
    // other groups, any variant of their color.
    function leadFirst(list, groupHandle, exactVid) {
      var groupIds = colorVariantIds[groupHandle] || [];
      var lead = null;
      if (exactVid != null) {
        list.some(function (n) {
          if (n.getAttribute("data-variant-image") === String(exactVid)) {
            lead = n;
            return true;
          }
          return false;
        });
      }
      if (!lead) {
        list.some(function (n) {
          var vid = n.getAttribute("data-variant-image");
          if (vid && groupIds.indexOf(vid) !== -1) {
            lead = n;
            return true;
          }
          return false;
        });
      }
      if (!lead) return list;
      return [lead].concat(
        list.filter(function (n) {
          return n !== lead;
        }),
      );
    }

    var final = [];
    if (buckets[selHandle]) {
      final = final.concat(leadFirst(buckets[selHandle], selHandle, variantId));
    }
    order.forEach(function (h) {
      if (h !== selHandle && buckets[h]) {
        final = final.concat(leadFirst(buckets[h], h, null));
      }
    });
    // Safety: any group whose color isn't in colorOrder still renders.
    Object.keys(buckets).forEach(function (h) {
      if (h !== selHandle && order.indexOf(h) === -1) {
        final = final.concat(buckets[h]);
      }
    });
    final = final.concat(ungrouped);

    final.forEach(function (node) {
      container.appendChild(node);
    });
    syncPdpThumbOrder();

    // Desktop: bring the horizontal track back to its start so the new
    // lead image is what's showing, and if the user has scrolled past the
    // gallery, bring it back into view (scroll-margin-top on .pdp-gallery
    // keeps it clear of the header).
    if (window.matchMedia("(min-width: 1001px)").matches) {
      // Plain assignment — smooth scrollTo is unreliable on the snap
      // container, and the reorder is instantaneous anyway.
      container.scrollLeft = 0;
      var gallery = document.querySelector(".pdp-gallery");
      if (gallery && gallery.getBoundingClientRect().top < 0) {
        gallery.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }

    // Mobile: reset the carousel to the first (new lead) slide + counter.
    refreshPdpCarousel();
  }

  // Thumbnail strip: click → scroll the matching .pdp-image into view.
  // Uses data-image-index to pair a thumb with its source image regardless
  // of DOM order (the hoist in filterImagesByOption1 can reorder images).
  //
  // Edge case for the FIRST visible image: scrollIntoView aligns the
  // image's top with the viewport's top, leaving the #product container's
  // padding-top ($hangline-product, ~104px) visible above it. That
  // looks wrong — clicking the first thumb should take you to the
  // page's actual top. Detect "is this the first non-filtered image"
  // and scroll the window to 0 instead.
  document.addEventListener("click", function (e) {
    var thumb = e.target.closest(".test-slideshow .mini-pic");
    if (!thumb) return;
    var idx = thumb.getAttribute("data-image-index");
    var img = document.querySelector(
      '.product-images .pdp-image[data-image-index="' + idx + '"]',
    );
    if (!img) return;
    var firstVisible = document.querySelector(
      ".product-images .pdp-image:not(.is-filtered-out)",
    );
    if (img === firstVisible) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      img.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  });

  // ── Click to advance the carousel ────────────────────────────────────
  // The main images no longer open a lightbox; a click/tap steps the gallery
  // forward one image (looping at the end). It's handled at the input source,
  // NOT with a delegated click listener here, so a drag never counts as a tap:
  //   • desktop — the drag IIFE's release() advances when the pointer didn't
  //     move (see "drag to scroll"), where it has edgeStep()/animateSettle().
  //   • mobile  — the carousel's touchend advances a genuine tap (no swipe).
  // Both paths loop back to the first image past the last.

  // Active-thumb tracking: watch which .pdp-image is most visible and mark
  // its matching mini-pic .is-active. Re-initialised lazily on first need.
  var thumbObserver = null;
  function initThumbObserver() {
    if (thumbObserver || !("IntersectionObserver" in window)) return;
    var images = document.querySelectorAll(".product-images .pdp-image");
    if (!images.length) return;
    thumbObserver = new IntersectionObserver(
      function (entries) {
        // Pick the entry with the largest intersection ratio that's visible.
        var best = null;
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          if (!best || entry.intersectionRatio > best.intersectionRatio) {
            best = entry;
          }
        });
        if (!best) return;
        var idx = best.target.getAttribute("data-image-index");
        document
          .querySelectorAll(".test-slideshow .mini-pic")
          .forEach(function (t) {
            t.classList.toggle(
              "is-active",
              t.getAttribute("data-image-index") === idx,
            );
          });
      },
      // Trigger when the image's middle crosses the viewport middle.
      { rootMargin: "-40% 0px -40% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    images.forEach(function (img) {
      thumbObserver.observe(img);
    });
  }

  // ── Video autoplay on scroll-into-view ───────────────────────────────
  // PDP videos should autoplay only when they're actually on screen (and
  // pause when scrolled out of view). They're always muted+looping in the
  // template, so this just toggles play()/pause() based on intersection.
  // Both surfaces that can hold video are covered: the main image stack
  // and the In Situ thumbnail grid (editorial-tagged videos).
  //
  // We also force `playsinline` on every video at init time —
  // Shopify's video_tag filter doesn't expose it as an option, but
  // iOS Safari refuses to autoplay inline without it (the video
  // would fullscreen on tap or just not play).
  var pdpVideoObserver = null;
  function initPdpVideoObserver() {
    if (pdpVideoObserver || !("IntersectionObserver" in window)) return;
    var videos = document.querySelectorAll(
      ".product-images .pdp-image video, .pdp-insitu-thumb video",
    );
    if (!videos.length) return;

    // Force playsinline + muted + loop, and never controls. Some
    // browsers/SDKs strip these, so we re-assert them defensively before any
    // play() call — muted is what makes autoplay permissible in the first
    // place.
    //
    // Video is ambient here: it reads as a moving photograph, so it carries
    // no playback chrome on any surface, hover included. removeAttribute is
    // belt-and-braces against a stray `controls` in uploaded markup.
    videos.forEach(function (v) {
      v.setAttribute("playsinline", "");
      v.setAttribute("webkit-playsinline", ""); // older iOS
      v.muted = true;
      v.loop = true;
      v.removeAttribute("controls");
      if (!v.hasAttribute("preload")) v.preload = "metadata";
    });

    pdpVideoObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          if (entry.isIntersecting) {
            // play() returns a promise — swallow rejection silently
            // (autoplay can be blocked even when muted in some
            // browsing modes; the user can still scroll past).
            var p = video.play();
            if (p && typeof p.catch === "function") p.catch(function () {});
          } else {
            video.pause();
          }
        });
      },
      // threshold: 0 fires on any intersection change — necessary for
      // videos taller than the viewport where the ratio can never
      // reach 0.25 because only the middle portion is ever visible.
      // isIntersecting alone is the play/pause signal.
      { threshold: 0 },
    );
    videos.forEach(function (v) {
      pdpVideoObserver.observe(v);
    });
  }

  // ── PDP mobile image carousel (vanilla transform slider) ──────────────────
  // Ported from the tabayer theme. On mobile (≤ $mq-tablet-max) the
  // .product-images stack is laid out as a horizontal flex track inside the
  // .pdp-gallery viewport (CSS, _pdp.scss); here we translate that track on
  // swipe, with a 12px peek of the next slide and the "X OF Y" counter below.
  // Desktop keeps the vertical stack (the transform is cleared above the BP).
  //
  // We reuse the existing .pdp-image nodes (no markup duplication) so variant
  // filtering + the lightbox keep working untouched. display:none slides drop
  // out of the flex row, so we index against the *visible* slides only.
  // Must match $pdp-mode-bp in _pdp.scss (the PDP desktop/mobile mode switch),
  // which is $mq-tablet-min. Was 1000 while the CSS grid stayed 2-column until
  // 769, so 770–1000 ran the carousel inside the desktop layout.
  var PDP_CAROUSEL_BP = 769;
  var pdpCarousel = null; // { gallery, track, current }

  function pdpVisibleSlides(track) {
    return Array.prototype.filter.call(
      track.querySelectorAll(".pdp-image"),
      function (el) {
        return !el.classList.contains("is-filtered-out");
      },
    );
  }

  function pdpCarouselUpdate() {
    if (!pdpCarousel) return;
    var slides = pdpVisibleSlides(pdpCarousel.track);
    var total = slides.length;
    var counter = document.querySelector(".pdp-mobile-counter");
    var totalEl = counter && counter.querySelector(".pdp-mobile-counter-total");
    var currentEl =
      counter && counter.querySelector(".pdp-mobile-counter-current");
    if (totalEl) totalEl.textContent = String(total);
    if (!total) return;

    if (pdpCarousel.current > total - 1) pdpCarousel.current = total - 1;
    if (pdpCarousel.current < 0) pdpCarousel.current = 0;

    if (window.innerWidth <= PDP_CAROUSEL_BP) {
      // One slide advance = slide width + its right-margin gap. Measured from
      // the live DOM so it tracks whatever the CSS slide width / gap are set to
      // (translateX % is relative to the track's own width = the gallery width).
      var trackW =
        pdpCarousel.track.getBoundingClientRect().width ||
        pdpCarousel.gallery.offsetWidth;
      var sample = slides[0];
      var slideW = sample.getBoundingClientRect().width;
      var gap = parseFloat(getComputedStyle(sample).marginRight) || 0;
      var wantPx = pdpCarousel.current * (slideW + gap);
      // The LAST slide rests RIGHT-aligned with a 3px gutter rather than
      // scrolling to the left edge like the others — otherwise it would leave
      // dead space on the right where a next-slide peek would be, since there's
      // no next slide. Cap the scroll so the last image's right edge stops 3px
      // from the gallery's right edge (which is the viewport edge — the gallery
      // bleeds to it on mobile). Intermediate slides are untouched: their
      // wantPx is always below this cap.
      var maxPx = Math.max(
        0,
        (total - 1) * (slideW + gap) + slideW - trackW + 3,
      );
      var px = Math.min(wantPx, maxPx);
      var pct = trackW ? (px / trackW) * 100 : 0;
      pdpCarousel.track.style.transform = "translateX(-" + pct + "%)";
    } else {
      // Desktop: clear the transform so the vertical stack lays out normally.
      pdpCarousel.track.style.transform = "";
    }
    if (currentEl) currentEl.textContent = String(pdpCarousel.current + 1);
  }

  function pdpCarouselGoTo(index) {
    if (!pdpCarousel) return;
    var total = pdpVisibleSlides(pdpCarousel.track).length;
    pdpCarousel.current = Math.max(0, Math.min(index, total - 1));
    pdpCarouselUpdate();
  }

  // Step one slide in `dir`, wrapping around the ends (tap-to-advance). Kept
  // separate from pdpCarouselGoTo, which clamps, so swipes still rest at the
  // ends while a tap cycles.
  function pdpAdvance(dir) {
    if (!pdpCarousel) return;
    var total = pdpVisibleSlides(pdpCarousel.track).length;
    if (total < 2) return;
    pdpCarouselGoTo((pdpCarousel.current + dir + total) % total);
  }

  function initPdpCarousel() {
    if (pdpCarousel) {
      pdpCarouselUpdate();
      return;
    }
    var gallery = document.querySelector(".pdp-gallery");
    var track = gallery && gallery.querySelector(".product-images");
    if (!gallery || !track) return;
    pdpCarousel = { gallery: gallery, track: track, current: 0 };

    // Swipe: advance once the horizontal drag passes a 50px threshold. Vertical
    // gestures are ignored so the page can still scroll. A tap (no meaningful
    // movement) advances one slide, looping — the mobile counterpart to the
    // desktop click-to-advance.
    var startX = 0,
      startY = 0,
      dragging = false,
      touchMoved = false;
    gallery.addEventListener(
      "touchstart",
      function (e) {
        if (!e.touches || !e.touches[0]) return;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        dragging = true;
        touchMoved = false;
      },
      { passive: true },
    );
    gallery.addEventListener(
      "touchmove",
      function (e) {
        if (!dragging || !e.touches || !e.touches[0]) return;
        var dx = startX - e.touches[0].clientX;
        var dy = startY - e.touches[0].clientY;
        // Any real movement disqualifies this gesture from being a tap — this
        // is what keeps a vertical page-scroll (dx small, dy large) from
        // advancing the carousel when the finger lifts.
        if (Math.abs(dx) > 10 || Math.abs(dy) > 10) touchMoved = true;
        if (Math.abs(dx) < Math.abs(dy)) return; // vertical → let it scroll
        if (Math.abs(dx) > 50) {
          // Swipe rests at the ends (clamped), mirroring the desktop drag; only
          // the discrete tap/click loops.
          pdpCarouselGoTo(pdpCarousel.current + (dx > 0 ? 1 : -1));
          dragging = false;
        }
      },
      { passive: true },
    );
    gallery.addEventListener("touchend", function () {
      // A tap that never became a swipe or a scroll → step forward, looping.
      if (dragging && !touchMoved) pdpAdvance(1);
      dragging = false;
    });

    window.addEventListener("resize", pdpCarouselUpdate);
    pdpCarouselUpdate();
  }

  // Reset to the first (now-featured) slide + refresh the counter after a
  // variant filter pass (filterImagesByOption1 hides/reorders slides).
  function refreshPdpCarousel() {
    if (!pdpCarousel) return;
    pdpCarousel.current = 0;
    pdpCarouselUpdate();
  }

  // CSS color fallback: returns the value lowercased and space-stripped so
  // basic CSS color names ("blue", "red") fill the swatch, anything else
  // falls through to the outlined ring.
  // Swatch resolution order (mirrors snippets/swatch-color.liquid). Returns
  //   { color: hex | "", image: swatch image URL | null }
  // where a non-null `image` renders a multi-color / split swatch from an image.
  //   1. Taxonomy colour metaobjects (Color + Light color), as
  //      PDP_PRODUCT.categorySwatches[value] = { c, img }.
  //   2. Shopify's per-option-value swatch, as PDP_PRODUCT.options[i].swatches[value].
  //   3. Theme palette (swatch_N_name + color_N), as PDP_PRODUCT.swatchSettings.
  // No CSS-name fallback: a value with no provided colour returns "" and the
  // swatch circle is omitted (matches swatch-color.liquid). A non-colour option
  // (e.g. Mounting) then reads as plain text, not an empty ring.
  function resolveSwatch(opt, value) {
    var P = window.PDP_PRODUCT || {};
    if (P.categorySwatches && P.categorySwatches[value]) {
      var cs = P.categorySwatches[value];
      return { color: cs.c, image: cs.img || null };
    }
    if (opt && opt.swatches && opt.swatches[value]) {
      return { color: opt.swatches[value], image: null };
    }
    if (P.swatchSettings && P.swatchSettings[value]) {
      return { color: P.swatchSettings[value], image: null };
    }
    return { color: "", image: null };
  }

  // Inline-style + class string for a swatch <span> (image-aware).
  function swatchSpanHtml(opt, value) {
    var sw = resolveSwatch(opt, value);
    // No colour and no image → no circle (the value shows as plain text).
    if (!sw.color && !sw.image) return "";
    var style = "--swatch-color: " + sw.color + ";";
    var cls = "pdp-picker-swatch";
    if (sw.image) {
      style += " --swatch-image: url('" + sw.image + "');";
      cls += " is-image";
    }
    return '<span class="' + cls + '" style="' + style + '"></span>';
  }

  // Update an existing swatch element in place (image-aware).
  function applySwatch(el, opt, value) {
    var sw = resolveSwatch(opt, value);
    // No colour/image for this value → hide the circle rather than draw an
    // empty ring (covers a mixed option whose values aren't all colours).
    if (!sw.color && !sw.image) {
      el.hidden = true;
      return;
    }
    el.hidden = false;
    el.style.setProperty("--swatch-color", sw.color);
    if (sw.image) {
      el.style.setProperty("--swatch-image", "url('" + sw.image + "')");
      el.classList.add("is-image");
    } else {
      el.style.removeProperty("--swatch-image");
      el.classList.remove("is-image");
    }
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return {
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      }[c];
    });
  }

  // The options a user can actually pick — i.e. the ones visible in the
  // panel. The current selection is hidden from the list (it's already shown
  // on the trigger; see _pdp.scss), so it must be skipped for focus + arrow
  // nav or keyboard users would land on a display:none element. Mirrors the
  // CSS rule exactly, including its :only-child guard: an option with a lone
  // value stays visible rather than leaving an empty panel.
  function visibleOptions(panel) {
    var all = Array.prototype.slice.call(
      panel.querySelectorAll(".pdp-picker-option"),
    );
    if (all.length <= 1) return all;
    return all.filter(function (o) {
      return o.getAttribute("aria-selected") !== "true";
    });
  }

  // Open a picker's panel and move focus into it. Returns the option
  // that gets focus so callers can chain (e.g. keyboard nav).
  function openPicker(trigger) {
    var picker = trigger.closest(".pdp-picker");
    var panel = picker.querySelector(".pdp-picker-panel");
    closeAllPickers();
    panel.hidden = false;
    trigger.setAttribute("aria-expanded", "true");
    // Focus the first SELECTABLE option. (The current selection is hidden
    // from the list, so we can't focus it — the trigger already announces it.)
    var opts = visibleOptions(panel);
    var selected = opts[0];
    if (selected) {
      // Update tabindex roving so only the focused option is in the
      // tab order. Then move DOM focus.
      panel.querySelectorAll(".pdp-picker-option").forEach(function (o) {
        o.tabIndex = -1;
      });
      selected.tabIndex = 0;
      selected.focus();
    }
    return selected;
  }

  // Commit a value to the picker: visually update the trigger, fire
  // variantChange(), close panels, restore focus to the trigger.
  function selectOption(option) {
    var picker = option.closest(".pdp-picker");
    var value = option.dataset.value;
    picker.querySelectorAll(".pdp-picker-option").forEach(function (o) {
      o.removeAttribute("aria-selected");
      o.tabIndex = -1;
    });
    option.setAttribute("aria-selected", "true");
    option.tabIndex = 0;
    var valueEl = picker.querySelector(".pdp-picker-trigger .pdp-picker-value");
    var swatch = picker.querySelector(".pdp-picker-trigger .pdp-picker-swatch");
    if (valueEl) valueEl.textContent = value;
    if (swatch) {
      var optIdx = parseInt(picker.dataset.optionIndex, 10);
      var optMeta =
        window.PDP_PRODUCT && window.PDP_PRODUCT.options
          ? window.PDP_PRODUCT.options[optIdx]
          : null;
      applySwatch(swatch, optMeta, value);
    }
    var trigger = picker.querySelector(".pdp-picker-trigger");
    closeAllPickers();
    if (trigger) trigger.focus();
    variantChange();
  }

  // Click delegation — open/close pickers, select options, close on outside click.
  document.addEventListener("click", function (e) {
    var trigger = e.target.closest(".pdp-picker-trigger");
    if (trigger) {
      e.preventDefault();
      var picker = trigger.closest(".pdp-picker");
      var panel = picker.querySelector(".pdp-picker-panel");
      var isOpen = !panel.hidden;
      if (isOpen) {
        closeAllPickers();
      } else {
        openPicker(trigger);
      }
      return;
    }

    var option = e.target.closest(".pdp-picker-option");
    if (option) {
      selectOption(option);
      return;
    }

    if (!e.target.closest(".pdp-picker")) {
      closeAllPickers();
    }
  });

  // Keyboard nav — WAI-ARIA combobox-with-listbox pattern. Without this
  // the custom dropdown is fully mouse-only (a11y blocker).
  //
  //  On the trigger button:
  //    Enter / Space / ArrowDown → open panel, focus selected option
  //    ArrowUp                   → open panel, focus last option
  //
  //  On an option (panel open):
  //    ArrowDown / ArrowUp       → move focus to next / previous option
  //    Home / End                → first / last option
  //    Enter / Space             → commit selection (selectOption)
  //    Escape                    → close, return focus to trigger
  //    Tab                       → close, allow native tab order
  document.addEventListener("keydown", function (e) {
    var trigger = e.target.closest(".pdp-picker-trigger");
    if (trigger) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        openPicker(trigger);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        openPicker(trigger);
        // Then jump to the last option.
        var panel = trigger
          .closest(".pdp-picker")
          .querySelector(".pdp-picker-panel");
        var opts = visibleOptions(panel);
        var last = opts[opts.length - 1];
        if (last) {
          panel.querySelectorAll(".pdp-picker-option").forEach(function (o) {
            o.tabIndex = -1;
          });
          last.tabIndex = 0;
          last.focus();
        }
        return;
      }
      return;
    }

    var option = e.target.closest(".pdp-picker-option");
    if (option) {
      var picker = option.closest(".pdp-picker");
      var panel = picker.querySelector(".pdp-picker-panel");
      var opts = visibleOptions(panel);
      var idx = opts.indexOf(option);

      function moveFocus(target) {
        if (!target) return;
        opts.forEach(function (o) {
          o.tabIndex = -1;
        });
        target.tabIndex = 0;
        target.focus();
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          moveFocus(opts[Math.min(idx + 1, opts.length - 1)]);
          return;
        case "ArrowUp":
          e.preventDefault();
          moveFocus(opts[Math.max(idx - 1, 0)]);
          return;
        case "Home":
          e.preventDefault();
          moveFocus(opts[0]);
          return;
        case "End":
          e.preventDefault();
          moveFocus(opts[opts.length - 1]);
          return;
        case "Enter":
        case " ":
          e.preventDefault();
          selectOption(option);
          return;
        case "Escape":
          e.preventDefault();
          var trig = picker.querySelector(".pdp-picker-trigger");
          closeAllPickers();
          if (trig) trig.focus();
          return;
        case "Tab":
          // Let native tab proceed; just close the open panel so it
          // doesn't visually float over subsequent focus.
          closeAllPickers();
          return;
      }
    }
  });

  function closeAllPickers() {
    document
      .querySelectorAll(".pdp-picker .pdp-picker-panel")
      .forEach(function (p) {
        p.hidden = true;
      });
    document.querySelectorAll(".pdp-picker-trigger").forEach(function (t) {
      t.setAttribute("aria-expanded", "false");
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();

// ─── Mega menu ─────────────────────────────────────────────────────────────

document.addEventListener("DOMContentLoaded", function () {
  var megaMenu = document.getElementById("mega-menu");
  var megaTriggers = document.querySelectorAll(".has-mega-menu");

  if (megaMenu && megaTriggers.length) {
    var closeTimer;

    var openMenu = function () {
      clearTimeout(closeTimer);
      megaMenu.classList.add("is-open");
      megaMenu.setAttribute("aria-hidden", "false");
    };

    var closeMenu = function () {
      closeTimer = setTimeout(function () {
        megaMenu.classList.remove("is-open");
        megaMenu.setAttribute("aria-hidden", "true");
      }, 80);
    };

    megaTriggers.forEach(function (trigger) {
      trigger.addEventListener("mouseenter", openMenu);
      trigger.addEventListener("mouseleave", closeMenu);
    });

    megaMenu.addEventListener("mouseenter", function () {
      clearTimeout(closeTimer);
    });
    megaMenu.addEventListener("mouseleave", closeMenu);
  }
});

// ─── Article body: inline-image normalisation ───────────────────────────────
// Wrap every image in a blog article's .article-content in a
// figure.article-figure — a consistent, classed parent container for
// layout / sizing, with a figcaption from the image's alt text when
// present. Shopify's rich-text editor wraps content images in a lone
// <p> or a lone alignment <div style="text-align:…">; in those cases we
// REPLACE the wrapper with the <figure> so it becomes a direct child of
// .article-content (gets the full 614px content width, and — for a
// <div> — escapes the `> div { max-width: 410px }` text constraint).
// Any other shape wraps in place. Already-wrapped images are skipped so
// re-runs no-op.
//
// Lives here (bundled, deferred) rather than as an inline section script
// because inline <script> in a section isn't a reliable execution point
// across Shopify's preview / dev contexts.
document.addEventListener("DOMContentLoaded", function () {
  var content = document.querySelector(".article-content");
  if (!content) return;

  Array.prototype.forEach.call(content.querySelectorAll("img"), function (img) {
    if (img.closest("figure")) return; // already wrapped

    var parent = img.parentNode;
    var figure = document.createElement("figure");
    figure.className = "article-figure";

    var loneWrapper =
      (parent.tagName === "P" || parent.tagName === "DIV") &&
      parent.children.length === 1 &&
      !parent.textContent.trim();

    if (loneWrapper && parent.parentNode) {
      parent.parentNode.insertBefore(figure, parent);
      figure.appendChild(img);
      parent.parentNode.removeChild(parent);
    } else {
      parent.insertBefore(figure, img);
      figure.appendChild(img);
    }

    var alt = img.getAttribute("alt");
    if (alt && alt.trim()) {
      var caption = document.createElement("figcaption");
      caption.className = "article-figcaption";
      caption.textContent = alt;
      figure.appendChild(caption);
    }
  });
});

// ─── PLP action bar: sort disclosure + S/M/L grid size ──────────────────────
// Markup: snippets/shop-actionbar.liquid. The sort options are plain links
// (?sort_by=…, server-side re-sort) — this only toggles the disclosure
// panel open/closed. The size toggle swaps .plp-size-s/-m/-l on the grid
// and persists the choice to localStorage so it sticks across the session.
document.addEventListener("DOMContentLoaded", function () {
  var actionbar = document.querySelector("[data-shop-actionbar]");
  if (!actionbar) return;

  // ── Sticky-stuck detection ──
  // Toggle .is-stuck on the action bar when it pins to the top, so its
  // bottom padding can grow (7px → 15px). A sentinel sits at the bar's
  // natural top; once it scrolls above the sticky threshold (the bar's
  // top: 64.5px) it stops intersecting and the bar is stuck.
  var stickySentinel = document.querySelector("[data-shop-actionbar-sentinel]");
  if (stickySentinel && "IntersectionObserver" in window) {
    var stuckObserver = new IntersectionObserver(
      function (entries) {
        actionbar.classList.toggle("is-stuck", !entries[0].isIntersecting);
      },
      { rootMargin: "-65px 0px 0px 0px", threshold: 0 },
    );
    stuckObserver.observe(stickySentinel);
  }

  // ── Sort disclosure ──
  var sortWrap = actionbar.querySelector("[data-sort]");
  if (sortWrap) {
    var toggle = sortWrap.querySelector(".shop-actionbar-sort-toggle");
    var panel = sortWrap.querySelector(".shop-actionbar-sort-panel");
    if (toggle && panel) {
      toggle.addEventListener("click", function () {
        var willOpen = panel.hidden;
        panel.hidden = !willOpen;
        // The +/− sign is an inline SVG (snippets/shop-actionbar.liquid); CSS
        // hides its vertical stroke off this attribute. Nothing to rewrite here
        // — setting textContent would delete the SVG.
        toggle.setAttribute("aria-expanded", willOpen ? "true" : "false");
      });
    }
  }

  // ── Size toggle (S / M / L) ──
  // The size class lives on <html> (plp-size-s/m/l) — set pre-paint by
  // the inline script in theme.liquid (no flash), and the CSS scopes
  // grid density to .plp-sizable off that html class. Here we just keep
  // the html class + the button states in sync on live toggles.
  var SIZE_KEY = "slj-plp-size";
  var sizeBtns = actionbar.querySelectorAll("[data-plp-size-value]");
  var html = document.documentElement;

  function currentSize() {
    if (html.classList.contains("plp-size-s")) return "s";
    if (html.classList.contains("plp-size-l")) return "l";
    return "m";
  }

  function applySize(size) {
    if (size !== "s" && size !== "m" && size !== "l") size = "m";
    html.classList.remove("plp-size-s", "plp-size-m", "plp-size-l");
    html.classList.add("plp-size-" + size);
    Array.prototype.forEach.call(sizeBtns, function (b) {
      b.setAttribute(
        "aria-pressed",
        b.getAttribute("data-plp-size-value") === size ? "true" : "false",
      );
    });
  }

  // Sync button states to whatever the inline script already applied.
  applySize(currentSize());

  Array.prototype.forEach.call(sizeBtns, function (b) {
    b.addEventListener("click", function () {
      var size = b.getAttribute("data-plp-size-value");
      applySize(size);
      try {
        localStorage.setItem(SIZE_KEY, size);
      } catch (e) {}
    });
  });
});

// ─── Editorial tile sizing (2×2 media + editorial product cards) ────────────
// Two things render as a 2×2 tile in desktop Medium: the editorial IMAGE tile
// (.editorial-image, from the collection's editorial_images) and an editorial
// PRODUCT card (.shop-thumb--editorial). Both must end exactly where the
// neighbouring cards' images end. The bottom row's height depends on that
// neighbour's text (1- vs 2-line title), which CSS can't read — so measure the
// bottom-row neighbour's image bottom and publish it as --editorial-img-h
// (consumed by .editorial-image media and .thumb-image / .hover-file). Re-run on
// resize, size toggle (html class flip), font load, and card appends.
document.addEventListener("DOMContentLoaded", function () {
  var grid = document.querySelector(".plp-sizable");
  if (!grid) return;

  function alignEditorial() {
    var editorials = grid.querySelectorAll(
      ".shop-thumb--editorial, .editorial-image",
    );
    if (!editorials.length) return;
    var desktopM =
      window.matchMedia("(min-width: 770px)").matches &&
      document.documentElement.classList.contains("plp-size-m");
    var normals = desktopM
      ? grid.querySelectorAll(
          ".shop-thumb:not(.shop-thumb--editorial) .thumb-image",
        )
      : [];

    // Floor: the height the editorial media WOULD have if a full second row of
    // products sat beside it — row 1's media + its text block + the row gap +
    // row 2's media. Used when there's no bottom-row neighbour to measure, so
    // the editorial can never compress below its 2-row span.
    //
    // This is the only thing holding the height of an editorial PRODUCT that
    // sits near the end of a collection (main-collection.liquid no longer
    // gates those on how many products follow), and it also covers a page
    // with a NEXT page rendered before infinite scroll has appended it.
    //
    // Stable, so the ResizeObserver below can't oscillate: it's derived from
    // ordinary 1x1 cards, whose height is intrinsic and unaffected by whatever
    // we set on the editorial.
    var floorH = 0;
    if (desktopM && normals.length) {
      var sampleImg = normals[0];
      var sampleCard = sampleImg.closest(".shop-thumb");
      var sampleText = sampleCard
        ? sampleCard.querySelector(".thumb-text")
        : null;
      var rowGap = parseFloat(getComputedStyle(grid).rowGap) || 0;
      var mediaH = sampleImg.getBoundingClientRect().height;
      var textH = sampleText ? sampleText.getBoundingClientRect().height : 0;
      if (mediaH > 0) floorH = Math.round(mediaH * 2 + textH + rowGap);
    }
    Array.prototype.forEach.call(editorials, function (ed) {
      if (!desktopM) {
        // S / L / mobile: normal card — the CSS treatment doesn't apply, so
        // clear any stale value.
        ed.style.removeProperty("--editorial-img-h");
        return;
      }
      // The editorial IMAGE tile is bare media; the editorial PRODUCT card
      // wraps its media in .thumb-image (with text below).
      var img = ed.classList.contains("editorial-image")
        ? ed.querySelector(
            "img, video, .editorial-media-image, .editorial-media-video",
          )
        : ed.querySelector(".thumb-image");
      if (!img) return;
      var edRect = ed.getBoundingClientRect();
      var imgTop = img.getBoundingClientRect().top;
      var mid = edRect.top + edRect.height / 2;
      // The neighbour image(s) in the editorial's BOTTOM row sit in the lower
      // half of its span; they share a row, so take the lowest bottom.
      var target = 0;
      Array.prototype.forEach.call(normals, function (n) {
        var r = n.getBoundingClientRect();
        if (r.top > mid && r.top < edRect.bottom && r.bottom > target) {
          target = r.bottom;
        }
      });
      if (target > 0) {
        ed.style.setProperty(
          "--editorial-img-h",
          Math.round(target - imgTop) + "px",
        );
      } else if (floorH > 0) {
        // No second-row neighbour to match — hold the full 2-row height rather
        // than letting the collapsed grid row squash the editorial.
        ed.style.setProperty("--editorial-img-h", floorH + "px");
      } else {
        ed.style.removeProperty("--editorial-img-h");
      }
    });
  }

  // rAF-debounced so layout reads settle and repeated triggers coalesce.
  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      alignEditorial();
    });
  }

  schedule();
  window.addEventListener("load", schedule);
  window.addEventListener("resize", schedule);
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(schedule);
  }
  // Width / content / text-wrap changes (setting --editorial-img-h never grows
  // the grid — the image stays within its 2-row span — so this can't loop).
  if (window.ResizeObserver) {
    new ResizeObserver(schedule).observe(grid);
  }
  if (window.MutationObserver) {
    // Cards appended by infinite scroll.
    new MutationObserver(schedule).observe(grid, { childList: true });
    // Size toggle flips the <html> plp-size-* class.
    new MutationObserver(schedule).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }
});

// ─── PLP L card carousel (alt-tag image system) ─────────────────────────────
// snippets/thumb-carousel.liquid renders the slides; _plp.scss shows them only
// in the desktop L (2-up) view. Delegated so infinite-scroll cards work
// without rebinding. preventDefault/stopPropagation keep the card's <a> from
// navigating when an arrow is clicked. Wraps around at the ends.
document.addEventListener("click", function (e) {
  var arrow = e.target.closest("[data-thumb-prev], [data-thumb-next]");
  if (!arrow) return;
  e.preventDefault();
  e.stopPropagation();
  var carousel = arrow.closest("[data-thumb-carousel]");
  var track = carousel && carousel.querySelector(".thumb-carousel-track");
  if (!track) return;
  // A swatch click may have laid a colorway image over the carousel — arrow
  // navigation dismisses it and resumes browsing the slides.
  var overlay = carousel.querySelector(".thumb-carousel-swatch-overlay");
  if (overlay) overlay.remove();
  var count = track.children.length;
  if (count < 2) return;
  var idx = parseInt(carousel.getAttribute("data-idx"), 10) || 0;
  idx = arrow.hasAttribute("data-thumb-next")
    ? (idx + 1) % count
    : (idx - 1 + count) % count;
  carousel.setAttribute("data-idx", idx);
  track.style.transform = "translateX(-" + idx * 100 + "%)";
});

// ─── PLP L carousel: warm the slides on card hover ──────────────────────────
// Slides sit side by side in a flex track that's clipped to one slide, so
// every slide but the current one is parked outside the viewport and native
// lazy-loading leaves it alone. Clicking an arrow is therefore what STARTS the
// download — and going BACKWARDS is the worst case, because the previous slide
// from slide 1 is the LAST one: the furthest away, and the least likely to
// have been touched by anything. Hence "takes forever".
//
// So warm on hover, in reach order: prev and next are the only two slides a
// single arrow click can land on, so they go first (from the opening slide,
// "prev" IS the last image). The remainder follows, so a second click is warm
// too.
//
// Flipping loading= is what kicks off the fetch, and it beats a `new Image()`
// preload here: the element already carries the right srcset/sizes, so the
// browser picks the same entry it would have picked anyway — no risk of
// warming a URL the slide then doesn't ask for.
(function () {
  var cardHoverTimer = null;

  function warmSlide(slide) {
    var img = slide && slide.querySelector('img[loading="lazy"]');
    if (!img) return; // video slides buffer via their own observer
    if ("fetchPriority" in img) img.fetchPriority = "low";
    img.loading = "eager";
  }

  function warmCarousel(carousel) {
    if (carousel.dataset.slidesWarmed) return;
    // Only the L view renders the carousel; in S/M it's display:none and
    // hovering a card there shouldn't pull a single byte.
    if (getComputedStyle(carousel).display === "none") return;
    carousel.dataset.slidesWarmed = "1";

    var track = carousel.querySelector(".thumb-carousel-track");
    var slides = track ? Array.prototype.slice.call(track.children) : [];
    if (slides.length < 2) return;

    var n = slides.length;
    var idx = parseInt(carousel.getAttribute("data-idx"), 10) || 0;
    warmSlide(slides[(idx + 1) % n]); // next
    warmSlide(slides[(idx - 1 + n) % n]); // prev — the last slide, from idx 0
    for (var i = 0; i < n; i++) warmSlide(slides[i]); // then the rest
  }

  document.addEventListener("pointerover", function (e) {
    if (e.pointerType && e.pointerType !== "mouse") return;
    var carousel =
      e.target.closest && e.target.closest("[data-thumb-carousel]");
    clearTimeout(cardHoverTimer);
    if (!carousel) return;
    cardHoverTimer = setTimeout(function () {
      warmCarousel(carousel);
    }, 120);
  });
})();

// ─── PLP L carousel: editorial video slides ─────────────────────────────────
// Videos tagged Editorial One–Five render as carousel slides (see
// snippets/thumb-carousel-slide.liquid). Same contract as the PDP: muted,
// looping, and playing only while genuinely on screen.
//
// One observer covers every case, because intersection is computed through
// ancestor clipping: a slide translated out of the track's overflow:hidden
// box reports as not intersecting, and so does the whole carousel in the
// S/M/mobile views where it's display:none. So the only thing that ever
// plays is the current slide of an on-screen card in the L view.
(function () {
  if (!("IntersectionObserver" in window)) return;

  var bound = new WeakSet();
  var io = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          // Autoplay can still be refused (muted or not) in some browsing
          // modes — swallow it, the card just shows a still frame.
          var p = entry.target.play();
          if (p && typeof p.catch === "function") p.catch(function () {});
        } else {
          entry.target.pause();
        }
      });
    },
    { threshold: 0 },
  );

  function bindSlideVideos() {
    var vids = document.querySelectorAll(".thumb-carousel-slide video");
    for (var i = 0; i < vids.length; i++) {
      var v = vids[i];
      if (bound.has(v)) continue;
      bound.add(v);
      v.setAttribute("playsinline", "");
      v.setAttribute("webkit-playsinline", ""); // older iOS
      v.muted = true;
      v.loop = true;
      io.observe(v);
    }
  }

  bindSlideVideos();

  // Infinite scroll appends cards — adopt their slides as they arrive.
  var grid = document.querySelector(".plp-sizable");
  if (grid && window.MutationObserver) {
    new MutationObserver(bindSlideVideos).observe(grid, {
      childList: true,
      subtree: true,
    });
  }
})();

// ─── Trade Inquiries: LEARN MORE ↓ smooth scroll ─────────────────────────────
// Anchors to #trade-body; scroll-margin-top on the target (_trade.scss) lands
// its top flush with the bottom of the fixed header. scrollIntoView respects
// that margin — this just swaps the native jump for a smooth scroll.
document.addEventListener("click", function (e) {
  var more = e.target.closest(".trade-hero-more");
  if (!more) return;
  var target = document.getElementById("trade-body");
  if (!target) return; // no body copy on the page — let the default happen
  e.preventDefault();
  target.scrollIntoView({ behavior: "smooth", block: "start" });
});

// ─── About: READ MORE smooth scroll ──────────────────────────────────────────
// Anchors to #about-body; scroll-margin-top on the target (_about.scss) lands
// its top clear of the fixed header. Mirrors the Trade LEARN MORE behavior.
document.addEventListener("click", function (e) {
  var more = e.target.closest(".about-hero-more");
  if (!more) return;
  var target = document.getElementById("about-body");
  if (!target) return;
  e.preventDefault();
  target.scrollIntoView({ behavior: "smooth", block: "start" });
});

// ─── PDP: tabbed description (ABOUT / DETAILS / COLLECTION) ─────────────────
// Markup: .pdp-tabs in main-product.liquid. Delegated; panels pair with
// buttons via data-pdp-tab.
document.addEventListener("click", function (e) {
  var tab = e.target.closest(".pdp-tab");
  if (!tab) return;
  var wrap = tab.closest(".pdp-tabs");
  if (!wrap) return;
  var key = tab.getAttribute("data-pdp-tab");
  wrap.querySelectorAll(".pdp-tab").forEach(function (b) {
    var active = b === tab;
    b.classList.toggle("is-active", active);
    b.setAttribute("aria-selected", active ? "true" : "false");
  });
  wrap.querySelectorAll(".pdp-tab-panel").forEach(function (p) {
    p.classList.toggle("is-active", p.getAttribute("data-pdp-tab") === key);
  });
  // Scrolled panels start at the top when switching.
  var panels = wrap.querySelector(".pdp-tabs-panels");
  if (panels) panels.scrollTop = 0;
});

// ─── PDP: IN SITU lightbox ───────────────────────────────────────────────────
// Editorial-tagged media (rail thumbs → #pdp-insitu-overlay). No arrow
// controls by design: clicking advances to the next, looping.
// CLOSE / Escape / backdrop dismiss. Body scroll locked via the same
// pdp-overlay-open class as the main lightbox.
//
// The set can mix photos and videos. The overlay holds one <img> and one
// <video>; each item shows the element it needs and hides the other.
(function () {
  var insituIdx = 0;
  var insituTrigger = null;

  function insituThumbs() {
    return Array.prototype.slice.call(
      document.querySelectorAll(".pdp-insitu-thumb"),
    );
  }

  // ── Full-size source URL ────────────────────────────────────────────────
  // data-full is rendered as ?width=2000, but the viewer is sized by HEIGHT
  // (height:100dvh; width:auto), so width is the wrong axis to ask on: a
  // portrait editorial arrives ~25% heavier than any screen needs, and a
  // landscape one would come back far shorter than the viewport and render
  // soft. Ask for the height we're actually going to paint.
  //
  // Snapped to a 200px step so ordinary window resizes reuse one cache entry
  // instead of pulling a new file per pixel, and capped so a large display
  // can't request something enormous.
  function insituFullUrl(thumb) {
    var base = thumb && thumb.getAttribute("data-full");
    if (!base) return ""; // videos carry their own <source> set
    var dpr = window.devicePixelRatio || 1;
    var need = Math.ceil((window.innerHeight || 900) * dpr);
    var h = Math.min(Math.ceil(need / 200) * 200, 2400);
    return base.replace(/([?&])width=\d+/, "$1height=" + h);
  }

  // ── Hover-intent preload ────────────────────────────────────────────────
  // The full editorial is ~300KB and used to be fetched only once the viewer
  // was already open, so it opened onto an empty frame while the bytes
  // arrived — the reported lag. Warm it on hover instead, which buys the
  // whole dwell-then-click interval.
  //
  // The delay is the "intent" part: the thumbs are a tight 4-up grid, so a
  // pointer crossing it on the way somewhere else would otherwise kick off
  // every request in the row.
  var insituHoverTimer = null;
  var insituWarmed = {};

  function insituPreload(url) {
    if (!url || insituWarmed[url]) return;
    insituWarmed[url] = true;
    var pre = new Image();
    pre.src = url;
  }

  document.addEventListener("pointerover", function (e) {
    // Touch fires a synthetic pointerover right before click — too late to
    // help, and it would preload whatever the finger last brushed.
    if (e.pointerType && e.pointerType !== "mouse") return;
    var thumb = e.target.closest && e.target.closest(".pdp-insitu-thumb");
    if (!thumb) return;
    clearTimeout(insituHoverTimer);
    insituHoverTimer = setTimeout(function () {
      insituPreload(insituFullUrl(thumb));
    }, 100);
  });

  document.addEventListener("pointerout", function (e) {
    if (e.target.closest && e.target.closest(".pdp-insitu-thumb")) {
      clearTimeout(insituHoverTimer);
    }
  });

  // Touch/pen have no hover, but pointerdown still lands well ahead of the
  // click — take the head start that's on offer.
  document.addEventListener("pointerdown", function (e) {
    var thumb = e.target.closest && e.target.closest(".pdp-insitu-thumb");
    if (thumb) insituPreload(insituFullUrl(thumb));
  });

  function insituShow(idx) {
    var overlay = document.getElementById("pdp-insitu-overlay");
    var thumbs = insituThumbs();
    if (!overlay || !thumbs.length) return;
    insituIdx = ((idx % thumbs.length) + thumbs.length) % thumbs.length;

    var thumb = thumbs[insituIdx];
    var img = overlay.querySelector(".pdp-insitu-overlay-img");
    var video = overlay.querySelector(".pdp-insitu-overlay-video");
    var isVideo = thumb.getAttribute("data-insitu-type") === "video";

    if (isVideo && video) {
      // Adopt the thumb's <source> set rather than fetching a "full size"
      // URL: Shopify serves the same renditions whatever the thumb's
      // display size, so there is no larger version to ask for — and the
      // thumb has already buffered them while playing in the rail.
      // Swapping sources requires an explicit load(); the element won't
      // re-resolve them on its own.
      var thumbVideo = thumb.querySelector("video");
      var key = String(insituIdx);
      if (thumbVideo && video.getAttribute("data-src-key") !== key) {
        video.innerHTML = thumbVideo.innerHTML;
        video.setAttribute("data-src-key", key);
        video.load();
      }
      // Borrow the thumb's known dimensions as an explicit ratio. Until a
      // <video> loads metadata it reports a 300x150 intrinsic size, so
      // `width: auto` would briefly resolve to 300px and then jump to the
      // real width. The thumb has already played in the rail, so its
      // dimensions are known now and the overlay can open at the right size.
      if (thumbVideo && thumbVideo.videoWidth) {
        video.style.aspectRatio =
          thumbVideo.videoWidth + " / " + thumbVideo.videoHeight;
      } else {
        video.style.removeProperty("aspect-ratio");
      }
      video.muted = true; // autoplay is only permitted while muted
      video.hidden = false;
      if (img) {
        img.hidden = true;
        img.removeAttribute("src");
      }
      var p = video.play();
      if (p && typeof p.catch === "function") p.catch(function () {});
    } else if (img) {
      // Same builder the hover preload used — identical URL, so a warmed
      // image is a cache hit and the viewer paints immediately.
      img.src = insituFullUrl(thumb);
      var thumbImg = thumb.querySelector("img");
      img.alt = (thumbImg && thumbImg.getAttribute("alt")) || "";
      img.hidden = false;
      // Just pause — the sources stay put so stepping back onto this video
      // resumes without a reload. insituClose() does the full teardown.
      if (video) {
        video.pause();
        video.hidden = true;
      }
    }

    var current = overlay.querySelector(".pdp-insitu-overlay-current");
    var total = overlay.querySelector(".pdp-insitu-overlay-total");
    if (current) current.textContent = String(insituIdx + 1);
    if (total) total.textContent = String(thumbs.length);

    // Preload the next image so click-to-advance feels instant. Videos return
    // "" from insituFullUrl and are skipped — their sources are already in the
    // DOM, and new Image() would only fetch a video URL as a broken image.
    // Routed through insituPreload so it shares the warmed set with the hover
    // preload and can't re-request something already in flight.
    insituPreload(insituFullUrl(thumbs[(insituIdx + 1) % thumbs.length]));
  }

  function insituOpen(idx, trigger) {
    var overlay = document.getElementById("pdp-insitu-overlay");
    if (!overlay) return;
    // Escape ancestor stacking contexts (same fix as the main lightbox).
    if (overlay.parentNode !== document.body) {
      document.body.appendChild(overlay);
    }
    // Unhide before populating: insituShow() may call play(), and browsers
    // are within their rights to refuse a display:none video. Both happen in
    // the same task, so the empty overlay never gets painted.
    overlay.hidden = false;
    insituShow(idx);
    document.body.classList.add("pdp-overlay-open");
    insituTrigger = trigger || document.activeElement;
    var closeBtn = overlay.querySelector(".pdp-insitu-overlay-close");
    if (closeBtn) closeBtn.focus();
  }

  function insituClose() {
    var overlay = document.getElementById("pdp-insitu-overlay");
    if (!overlay || overlay.hidden) return;
    overlay.hidden = true;
    document.body.classList.remove("pdp-overlay-open");
    var img = overlay.querySelector(".pdp-insitu-overlay-img");
    if (img) img.removeAttribute("src");
    // Drop the sources, not just playback: a paused <video> that still has a
    // src keeps buffering in the background after the viewer is gone.
    var video = overlay.querySelector(".pdp-insitu-overlay-video");
    if (video) {
      video.pause();
      video.hidden = true;
      video.removeAttribute("data-src-key");
      video.style.removeProperty("aspect-ratio");
      video.innerHTML = "";
      video.load();
    }
    if (insituTrigger && typeof insituTrigger.focus === "function") {
      insituTrigger.focus();
    }
    insituTrigger = null;
  }

  document.addEventListener("click", function (e) {
    // Open from a rail thumb.
    var thumb = e.target.closest(".pdp-insitu-thumb");
    if (thumb) {
      e.preventDefault();
      insituOpen(
        parseInt(thumb.getAttribute("data-insitu-index"), 10) || 0,
        thumb,
      );
      return;
    }
    var overlay = document.getElementById("pdp-insitu-overlay");
    if (!overlay || overlay.hidden) return;
    // Click the media → next (loops); anywhere else in the overlay → close.
    // The overlay video renders without controls, so clicks reach it the
    // same way they reach the image.
    if (
      e.target.closest(".pdp-insitu-overlay-img, .pdp-insitu-overlay-video")
    ) {
      insituShow(insituIdx + 1);
      return;
    }
    if (e.target.closest(".pdp-insitu-overlay")) {
      insituClose();
    }
  });

  // Keyboard controls — active only while the viewer is open. ← / → step
  // through the media, looping exactly like clicking does (insituShow wraps
  // both directions); Home / End jump to the first / last; Escape closes. The
  // gallery's own arrow handlers bail on the pdp-overlay-open body class (set
  // while this viewer is open), so there's no double-advance, and
  // preventDefault stops the arrows from also scrolling the page.
  document.addEventListener("keydown", function (e) {
    var overlay = document.getElementById("pdp-insitu-overlay");
    if (!overlay || overlay.hidden) return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    switch (e.key) {
      case "Escape":
        insituClose();
        break;
      case "ArrowRight":
        insituShow(insituIdx + 1);
        break;
      case "ArrowLeft":
        insituShow(insituIdx - 1);
        break;
      case "Home":
        insituShow(0);
        break;
      case "End": {
        var thumbs = insituThumbs();
        if (thumbs.length) insituShow(thumbs.length - 1);
        break;
      }
      default:
        return;
    }
    e.preventDefault();
  });
})();

// ─── PDP: warm the whole main image stack ───────────────────────────────────
// The stack is a HORIZONTAL scroller, so every image past the first screenful
// sits outside the viewport — and native lazy-loading, which only knows about
// vertical proximity in the usual case, won't fetch them until the carousel is
// actually dragged there. The result is that images arrive exactly when you're
// already looking at them. On mobile it's worse: only slide 1 is on screen, so
// every other image waits for a swipe.
//
// These are the most important images on the page, so nothing should wait for
// an interaction. Promote the rest to eager once the page has loaded.
//
// Deliberately AFTER the load event, and at low fetch priority: image 1 is the
// LCP candidate, and making it share bandwidth with images nobody has asked to
// see yet would trade a fast first paint for a fast tenth one. By the time
// anyone drags (a second or more), the tail is already in place. DOM order is
// preserved, so the near images — the first you'd scroll to — land first.
function warmPdpStack() {
  var imgs = document.querySelectorAll(
    '.product-images .pdp-image img[loading="lazy"]',
  );
  for (var i = 0; i < imgs.length; i++) {
    // Low priority first, so flipping loading= can't outrank anything still
    // in flight. Setting loading to eager is what actually kicks off the
    // fetch for an image the lazy heuristic has been sitting on.
    if ("fetchPriority" in imgs[i]) imgs[i].fetchPriority = "low";
    imgs[i].loading = "eager";
  }
}
var pdpStackWarmed = false;
function warmPdpStackOnce() {
  if (pdpStackWarmed) return;
  pdpStackWarmed = true;
  warmPdpStack();
}
if (document.querySelector(".product-images")) {
  if (document.readyState === "complete") warmPdpStackOnce();
  else {
    window.addEventListener("load", warmPdpStackOnce);
    // Belt and braces. `load` waits on every last subresource, so one slow
    // third-party script can hold it open for many seconds — and the gallery
    // would sit there lazy the whole time, waiting on something with nothing
    // to do with it. Whichever fires first wins; low fetch priority means the
    // early case still can't elbow past the LCP image.
    setTimeout(warmPdpStackOnce, 4000);
  }
}

// ─── PDP: desktop horizontal gallery — end-of-track cap ─────────────────────
// The last image's resting position (right edge 3px inside the track) is now
// produced entirely in CSS by a fixed zero-width ::after cap plus the flex gap
// — see the .product-images ::after rule in _pdp.scss. No JS sizing needed:
// the gutter is constant regardless of which image is last after a variant
// filter/regroup, so the old updatePdpTrackTail() (and its resize/load hooks)
// is gone.

// ─── PDP: desktop horizontal gallery — drag to scroll ───────────────────────
// The desktop image track (.product-images) is a native overflow-x scroller
// with scroll-snap (see _pdp.scss). Mouse dragging is 1:1: CSS snap is
// DISABLED for the duration of the drag (mandatory snap re-snaps on every
// scrollLeft write, which makes the track jump between points instead of
// following the pointer), the track scrolls exactly as far as the pointer
// moves, and on release it smooth-settles to the nearest slide edge before
// snap is restored (so wheel / trackpad scrolling keeps native snapping).
// A drag that actually moved suppresses the click so the lightbox doesn't
// open on release. Mobile/tablet (≤1000px) keep the transform carousel —
// this no-ops there via the matchMedia guard.
(function () {
  var track = document.querySelector(".pdp-gallery .product-images");
  if (!track) return;
  var down = false;
  var moved = false;
  var startX = 0;
  var startLeft = 0;
  var activePointer = null;
  var settleRaf = null;

  // Travel direction (+1 forward / -1 back / 0 idle). Set SYNCHRONOUSLY at
  // the input source — drag reads it from pointer movement, wheel from the
  // wheel delta — because async scroll events lag (and don't fire at all for
  // programmatic scroll), which would leave it stale at the settle instant.
  var scrollDir = 0;

  // Each slide's left offset within the scroll range, measured from live
  // rects (offsetLeft baselines vary with positioned ancestors).
  function slideEdges() {
    var trackLeft = track.getBoundingClientRect().left;
    var base = track.scrollLeft;
    var edges = [];
    track.querySelectorAll(".pdp-image").forEach(function (slide) {
      edges.push(slide.getBoundingClientRect().left - trackLeft + base);
    });
    return edges;
  }

  // Directional intent: settle to the geometrically nearest slide edge, but
  // never snap *against* the direction you were travelling (unless you're
  // basically already on that edge, within TOL). So a partial push forward
  // commits to the edge ahead instead of yanking back to the one behind.
  var SETTLE_TOL = 24;
  function settleTargetLeft(dir, ref) {
    var cur = ref == null ? track.scrollLeft : ref;
    var edges = slideEdges();
    if (!edges.length) return null;
    var below = null;
    var above = null;
    edges.forEach(function (e) {
      if (e <= cur + 0.5 && (below === null || e > below)) below = e;
      if (e >= cur - 0.5 && (above === null || e < above)) above = e;
    });
    if (below === null) below = above;
    if (above === null) above = below;
    if (below === null) return null;
    var nearest = cur - below <= above - cur ? below : above;
    if (dir > 0 && nearest === below && cur - below > SETTLE_TOL) return above;
    if (dir < 0 && nearest === above && above - cur > SETTLE_TOL) return below;
    return nearest;
  }

  // Settle animation — rAF + scrollLeft assignment (native smooth scrollTo
  // proved unreliable on a snap container mid-toggle). Snap is restored the
  // moment the animation lands, exactly on a slide edge, so there's no jump.
  function restoreSnap() {
    track.style.scrollSnapType = "";
  }

  // `gentle` (used by the wheel-idle settle) stretches the glide and uses a
  // softer ease-out so it eases toward the edge rather than snapping — the
  // drag release calls it without the flag to keep its crisp snap.
  function animateSettle(target, gentle) {
    if (settleRaf) cancelAnimationFrame(settleRaf);
    var from = track.scrollLeft;
    var delta = target - from;
    // rAF doesn't tick in hidden/backgrounded tabs — land instantly there
    // so the track never strands mid-way with snap disabled.
    if (Math.abs(delta) < 1 || document.hidden) {
      track.scrollLeft = target;
      restoreSnap();
      return;
    }
    // Duration scales gently with distance; clamped so short corrections
    // feel crisp and long ones don't drag on. The gentle variant is longer
    // so the motion reads as a soft glide, not a grab.
    var duration = gentle
      ? Math.min(700, Math.max(400, Math.abs(delta) * 0.8))
      : Math.min(450, Math.max(200, Math.abs(delta) * 0.5));
    // ease-out quart (gentle) decelerates more softly than ease-out cubic.
    var power = gentle ? 4 : 3;
    var t0 = null;
    function step(ts) {
      if (t0 === null) t0 = ts;
      var p = Math.min(1, (ts - t0) / duration);
      var eased = 1 - Math.pow(1 - p, power);
      track.scrollLeft = from + delta * eased;
      if (p < 1) {
        settleRaf = requestAnimationFrame(step);
      } else {
        settleRaf = null;
        restoreSnap();
      }
    }
    settleRaf = requestAnimationFrame(step);
  }

  // Images are draggable by default — that ghost-drag fights scroll-drag.
  track.addEventListener("dragstart", function (e) {
    e.preventDefault();
  });

  track.addEventListener("pointerdown", function (e) {
    if (!window.matchMedia("(min-width: 1001px)").matches) return;
    if (e.button !== 0) return;
    down = true;
    moved = false;
    startX = e.clientX;
    startLeft = track.scrollLeft;
    activePointer = e.pointerId;
    if (settleRaf) {
      cancelAnimationFrame(settleRaf);
      settleRaf = null;
    }
    cancelWheel(); // a grab interrupts any wheel glide/settle
    // 1:1 dragging — see block comment.
    track.style.scrollSnapType = "none";
  });

  track.addEventListener("pointermove", function (e) {
    if (!down) return;
    var dx = e.clientX - startX;
    if (!moved && Math.abs(dx) > 4) {
      moved = true;
      // Capture only once a real drag starts. Capturing on pointerdown
      // retargets the ensuing click to the track itself, which broke the
      // lightbox (its delegated handler needs the image as the target).
      try {
        track.setPointerCapture(activePointer);
      } catch (err) {}
    }
    if (moved) {
      var next = startLeft - dx;
      // Record travel direction synchronously — scroll events are async, so
      // relying on them would leave scrollDir stale at the release instant.
      if (Math.abs(next - track.scrollLeft) > 0.5) {
        scrollDir = next > track.scrollLeft ? 1 : -1;
      }
      track.scrollLeft = next;
    }
  });

  function release() {
    if (!down) return;
    down = false;
    try {
      if (activePointer != null) track.releasePointerCapture(activePointer);
    } catch (err) {}
    activePointer = null;
    if (moved) {
      var left = settleTargetLeft(scrollDir);
      if (left != null) {
        // Clamp to the scroll range. Near the end the nearest slide's LEFT
        // edge sits past max scroll (the last image is narrower than the
        // track), so an unclamped target would tween toward an unreachable
        // point and the browser would clamp each assignment — landing right,
        // but with the ease cut short. Clamping lets it glide cleanly to the
        // last image's right-aligned rest (the 3px-gutter end position). The
        // wheel-idle path already clamps via wclamp().
        animateSettle(Math.max(0, Math.min(wheelMax(), left)));
      } else {
        restoreSnap();
      }
    } else {
      // A tap, not a drag → advance one image, looping at the end (replaces
      // the old click-to-open-lightbox). This IIFE is desktop-only (pointerdown
      // is gated to min-width:1001px), so no viewport check is needed here.
      advanceGallery();
    }
  }

  // Step the gallery forward one image and settle there. Past the last image
  // (where the forward edge is beyond max scroll, so stepping wouldn't move)
  // it wraps to the first. Snap is restored by animateSettle on landing.
  function advanceGallery() {
    var edges = slideEdges();
    if (edges.length < 2) {
      restoreSnap();
      return;
    }
    var max = wheelMax();
    var next = edgeStep(1);
    if (next != null) next = Math.max(0, Math.min(max, next));
    // At/against the last image's right-aligned rest, the forward step clamps
    // to where we already are — treat that as "reached the end" and loop.
    if (next == null || Math.abs(next - track.scrollLeft) < 2) next = edges[0];
    scrollDir = next >= track.scrollLeft ? 1 : -1;
    cancelWheel();
    animateSettle(next);
  }
  track.addEventListener("pointerup", release);
  track.addEventListener("pointercancel", release);
  // Before a drag starts the pointer isn't captured, so a press that slips
  // off the track would otherwise never release (snap left disabled).
  document.addEventListener("pointerup", release);
  document.addEventListener("pointercancel", release);

  // Wheel: own the horizontal scroll so it feels like ONE continuous motion
  // that carries and rests into the snap — no native-scroll-then-robot-takes-
  // over handoff. We ease a `wpos` toward a `wtarget`: each wheel notch nudges
  // the target and the rAF loop eases the real scrollLeft toward it; when input
  // idles we simply retarget to the nearest slide edge (in the travel
  // direction) and the SAME easing carries the motion straight into it. Because
  // the target just changes — the loop never stops and restarts — there's no
  // pause and no velocity discontinuity.
  //
  // Only HORIZONTAL-dominant wheels are captured (preventDefault); a vertical
  // wheel scrolls the page as usual.
  var WHEEL_GAIN = 1.1; // how far one notch pushes the target
  var WHEEL_EASE = 0.14; // position→target chase per frame (lower = smoother)
  var wpos = track.scrollLeft;
  var wtarget = track.scrollLeft;
  var wheelRaf = null;
  var wheelIdle = null;

  function wheelMax() {
    return track.scrollWidth - track.clientWidth;
  }
  function wclamp(x) {
    return Math.max(0, Math.min(wheelMax(), x));
  }
  function wheelLoop() {
    var d = wtarget - wpos;
    // rAF is frozen in hidden/backgrounded tabs — land instantly there so the
    // track never strands mid-glide (mirrors animateSettle).
    if (Math.abs(d) < 0.4 || document.hidden) {
      wpos = wtarget;
      track.scrollLeft = wpos;
      wheelRaf = null;
      return;
    }
    wpos += d * WHEEL_EASE;
    track.scrollLeft = wpos;
    wheelRaf = requestAnimationFrame(wheelLoop);
  }
  function ensureWheelLoop() {
    // rAF is frozen in hidden tabs — run the loop synchronously so it lands
    // at the target instead of stalling (wheelLoop's guard makes it one shot).
    if (document.hidden) {
      wheelLoop();
      return;
    }
    if (wheelRaf == null) wheelRaf = requestAnimationFrame(wheelLoop);
  }
  function cancelWheel() {
    if (wheelRaf != null) {
      cancelAnimationFrame(wheelRaf);
      wheelRaf = null;
    }
    if (wheelIdle) {
      clearTimeout(wheelIdle);
      wheelIdle = null;
    }
  }

  track.addEventListener(
    "wheel",
    function (e) {
      if (!window.matchMedia("(min-width: 1001px)").matches) return;
      if (down) return; // dragging owns the motion
      // Horizontal intent only — let clearly-vertical wheels scroll the page.
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();
      // Normalize line/page delta modes to pixels.
      var unit =
        e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? track.clientWidth : 1;
      var dx = e.deltaX * unit;
      if (dx === 0) return;
      scrollDir = dx > 0 ? 1 : -1;
      // Any drag-settle in flight yields to the wheel.
      if (settleRaf) {
        cancelAnimationFrame(settleRaf);
        settleRaf = null;
      }
      // Resync when idle in case something else moved the track.
      if (wheelRaf == null) {
        wpos = track.scrollLeft;
        wtarget = wpos;
      }
      wtarget = wclamp(wtarget + dx * WHEEL_GAIN);
      ensureWheelLoop();
      // Retarget to the snap edge shortly after input stops; the running ease
      // glides into it with no handoff.
      if (wheelIdle) clearTimeout(wheelIdle);
      wheelIdle = setTimeout(function () {
        wheelIdle = null;
        if (down) return;
        var edge = settleTargetLeft(scrollDir, wtarget);
        if (edge != null) {
          wtarget = wclamp(edge);
          ensureWheelLoop();
        }
      }, 90);
    },
    { passive: false },
  );

  // Suppress the click that follows a real drag (capture phase beats the
  // lightbox's document-level handler).
  track.addEventListener(
    "click",
    function (e) {
      if (!moved) return;
      moved = false;
      e.preventDefault();
      e.stopPropagation();
    },
    true,
  );

  // ── Keyboard controls ────────────────────────────────────────────────────
  // The track is a scrollable region, so it should be focusable and operable
  // by keyboard. Arrows step one image at a time (a plain focusable scroller
  // would otherwise creep by a few px per press), Home/End jump to the ends.
  // Scoped to the focused track — no document-level listener — so it can't
  // hijack page scrolling, the variant picker's own arrow handling, or the
  // lightbox. Reuses the same settle animation as drag/wheel.
  // tabindex -1: focusable programmatically (hover / click) so the key handler
  // below can run, but NOT a tab stop — so it needs no visible focus ring, and
  // we don't add a silent, indicator-less stop to the tab order. Keyboard users
  // aren't shut out: ← / → drive the gallery page-wide (see the document
  // listener below), no focusing required.
  track.tabIndex = -1;
  track.setAttribute("role", "region");
  track.setAttribute("aria-label", "Product images — use arrow keys to browse");

  // The next slide edge in `dir` from the current position. Unlike
  // settleTargetLeft (nearest edge, direction-biased) this always advances a
  // full slide. Returns null at the ends.
  function edgeStep(dir) {
    var cur = track.scrollLeft;
    var TOL = 2; // ignore sub-pixel drift so we never "step" onto ourselves
    var best = null;
    slideEdges().forEach(function (e) {
      if (dir > 0) {
        if (e > cur + TOL && (best === null || e < best)) best = e;
      } else {
        if (e < cur - TOL && (best === null || e > best)) best = e;
      }
    });
    return best;
  }

  track.addEventListener("keydown", function (e) {
    if (!window.matchMedia("(min-width: 1001px)").matches) return;
    // A lightbox owns the keyboard while it's open.
    if (document.body.classList.contains("pdp-overlay-open")) return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;

    var edges = slideEdges();
    if (!edges.length) return;
    var target = null;

    switch (e.key) {
      case "ArrowRight":
        target = edgeStep(1);
        scrollDir = 1;
        break;
      case "ArrowLeft":
        target = edgeStep(-1);
        scrollDir = -1;
        break;
      case "Home":
        target = edges[0];
        scrollDir = -1;
        break;
      case "End":
        target = edges[edges.length - 1];
        scrollDir = 1;
        break;
      default:
        return;
    }

    // Always preventDefault for these keys, even at the ends — otherwise the
    // browser's native scroller nudges the track off its snap position.
    e.preventDefault();
    if (target == null) return; // already at the first/last image
    cancelWheel();
    animateSettle(target);
  });

  // Hovering the gallery makes it the keyboard target, so ← / → AND Home/End
  // work without clicking or tabbing in first. Mouse only (touch has no hover,
  // and focusing there would pop the soft keyboard). We never yank focus out of
  // a field someone is typing in, and preventScroll stops focus() from jumping
  // the page.
  track.addEventListener("pointerenter", function (e) {
    if (e.pointerType && e.pointerType !== "mouse") return;
    if (!window.matchMedia("(min-width: 1001px)").matches) return;
    if (document.body.classList.contains("pdp-overlay-open")) return;
    var ae = document.activeElement;
    if (
      ae &&
      ae.closest &&
      ae.closest(
        "input, textarea, select, [contenteditable=''], [contenteditable='true']",
      )
    ) {
      return;
    }
    track.focus({ preventScroll: true });
  });

  // Page-level ← / → so the gallery responds on a fresh page load without
  // having to click or tab into it first. We deliberately DON'T autofocus the
  // track on load — that would steal focus from the top of the document (and
  // send the next Tab past the header). Instead we listen at the document and
  // hand the keys to the gallery only when nothing else wants them.
  //
  // Only ← / → : Home/End stay scoped to the focused track, since at page
  // level those belong to the browser (scroll to top/bottom).
  // This listener only exists on PDPs — the IIFE returns early without a track.
  document.addEventListener("keydown", function (e) {
    // The track's own handler runs first when it's focused; don't double-step.
    if (e.defaultPrevented) return;
    if (!window.matchMedia("(min-width: 1001px)").matches) return;
    if (document.body.classList.contains("pdp-overlay-open")) return;
    if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;

    // Never take arrows from a text field — ← / → move the caret there.
    var el = e.target;
    if (el && el.closest) {
      if (
        el.closest(
          "input, textarea, select, [contenteditable=''], [contenteditable='true']",
        )
      ) {
        return;
      }
      // Defer to the variant picker only while its panel is OPEN. The picker
      // never binds ← / → (trigger: Enter/Space/↑/↓; options: ↑/↓/Home/End/
      // Enter/Space/Esc/Tab), so a merely-focused trigger — e.g. focus returns
      // there right after choosing a colour — shouldn't block the gallery.
      var pk = el.closest(".pdp-picker");
      if (pk && pk.querySelector('.pdp-picker-trigger[aria-expanded="true"]')) {
        return;
      }
    }

    var dir = e.key === "ArrowRight" ? 1 : -1;
    var target = edgeStep(dir);
    if (target == null) return; // at an end — let the key through
    e.preventDefault();
    scrollDir = dir;
    cancelWheel();
    animateSettle(target);
  });

  // Prev / next arrow buttons (top-right of the gallery) — the same one-slide
  // edge step as the ← / → keys. They sit outside .product-images, so they
  // never trip the track's own tap-to-advance. No-op at the ends, where
  // edgeStep returns null.
  var arrowBar = document.querySelector(".pdp-gallery-arrows");
  if (arrowBar) {
    var prevBtn = arrowBar.querySelector(".pdp-gallery-arrow--prev");
    var nextBtn = arrowBar.querySelector(".pdp-gallery-arrow--next");
    var statusEl = arrowBar.querySelector(".pdp-gallery-status");
    var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    // Where an arrow press would actually land, or null if nowhere. edgeStep
    // alone isn't enough: the final slides sit past the end of the scroll
    // range, so it keeps returning edges the track can't reach — clamping
    // first is what makes "there is no next" detectable.
    function stepTarget(dir) {
      var t = edgeStep(dir);
      if (t == null) return null;
      t = Math.max(0, Math.min(wheelMax(), t));
      return Math.abs(t - track.scrollLeft) < 2 ? null : t;
    }

    // Disable whichever arrow has nowhere to go, and announce the landed
    // slide. Called after every settle and on plain scrolling, so the state
    // stays honest no matter how the track was moved (drag, wheel, keys).
    function syncArrows() {
      // Read this BEFORE toggling disabled — the browser blurs a button the
      // instant it's disabled, so afterwards activeElement is already <body>.
      var focused = document.activeElement;
      if (prevBtn) prevBtn.disabled = stepTarget(-1) == null;
      if (nextBtn) nextBtn.disabled = stepTarget(1) == null;
      // Stepping to an end disables the arrow you're standing on, which would
      // otherwise dump keyboard focus onto <body>. Hand it to the opposite
      // arrow so the tab position survives.
      if ((focused === prevBtn || focused === nextBtn) && focused.disabled) {
        var other = focused === nextBtn ? prevBtn : nextBtn;
        if (other && !other.disabled) other.focus();
      }
      if (!statusEl) return;
      var edges = slideEdges();
      if (!edges.length) return;
      // Nearest edge to the current position = the leading slide on screen.
      var cur = track.scrollLeft;
      var idx = 0;
      edges.forEach(function (e, i) {
        if (Math.abs(e - cur) < Math.abs(edges[idx] - cur)) idx = i;
      });
      // The last slides sit past the end of the scroll range, so the leading
      // slide never reaches the final index. At the end, say so outright —
      // otherwise "image 3 of 4" contradicts a disabled next arrow.
      if (nextBtn && nextBtn.disabled) idx = edges.length - 1;
      if (prevBtn && prevBtn.disabled) idx = 0;
      var msg = "Image " + (idx + 1) + " of " + edges.length;
      if (statusEl.textContent !== msg) statusEl.textContent = msg;
    }

    arrowBar.addEventListener("click", function (e) {
      var btn = e.target.closest(".pdp-gallery-arrow");
      if (!btn || btn.disabled) return;
      var dir = btn.classList.contains("pdp-gallery-arrow--next") ? 1 : -1;
      var target = stepTarget(dir);
      if (target == null) return;
      scrollDir = dir;
      cancelWheel();
      // Reduced motion: jump straight there rather than gliding. The other
      // input paths (drag, wheel) are direct manipulation, so they're exempt.
      if (reduceMotion.matches) {
        track.scrollLeft = target;
        restoreSnap();
      } else {
        animateSettle(target);
      }
      syncArrows();
    });

    // Covers every other way the track can move — drag, wheel, native snap,
    // and the rAF settle (which assigns scrollLeft, so it fires this too).
    track.addEventListener("scroll", syncArrows, { passive: true });
    window.addEventListener("resize", syncArrows);
    syncArrows();
  }
})();

// ─── Product rows: PDP-style drag / wheel / settle ──────────────────────────
// Gives every horizontally-scrolling product row (.product-row-track — the
// homepage Product Row, the About page rows and the Article related row) the
// same motion as the PDP main gallery above: 1:1 dragging, a wheel that reads
// as one continuous glide rather than native-scroll-then-snap, and a settle
// that respects travel direction instead of yanking backwards.
//
// Desktop only (min-width: 1001px), exactly like the gallery: on touch, the
// native momentum scroll beats anything we'd synthesize.
//
// Two deliberate differences from the gallery, because row cards are LINKS:
//   • no click-to-advance — a plain click must follow the card's <a>;
//   • a real drag suppresses the click so it can't navigate mid-swipe.
// Rows also skip the gallery's keyboard nav (several rows per page would fight
// over the arrow keys) and its end-cap.
//
// The tuning constants + easing mirror the gallery IIFE above. They're repeated
// rather than shared because the two have genuinely different feature sets;
// keep them in step, or fold both onto one scroller if this grows again.
function initRowScroller(track) {
  if (!track || track.dataset.rowScroller) return;
  track.dataset.rowScroller = "1";

  var SETTLE_TOL = 24;
  var WHEEL_GAIN = 1.1;
  var WHEEL_EASE = 0.14;

  var down = false;
  var moved = false;
  var startX = 0;
  var startLeft = 0;
  var activePointer = null;
  var settleRaf = null;
  var scrollDir = 0;
  var wpos = track.scrollLeft;
  var wtarget = track.scrollLeft;
  var wheelRaf = null;
  var wheelIdle = null;

  function isDesktop() {
    return window.matchMedia("(min-width: 1001px)").matches;
  }
  function maxScroll() {
    return track.scrollWidth - track.clientWidth;
  }
  function clampX(x) {
    return Math.max(0, Math.min(maxScroll(), x));
  }

  // Card left offsets within the scroll range, from live rects, measured to
  // the INSIDE of the track's left padding.
  //
  // The track is padded 3px, so card one's rect sits at scrollLeft 3 while the
  // row rests at 0. Settling to the raw rect would scroll that 3px gutter out
  // of view and strand the row one gutter short of its start, with no way
  // back. Subtracting the padding makes each edge the scroll position where
  // that card sits just inside the gutter — card one lands at 0, and every
  // other card keeps the same 3px inset instead of going flush.
  function slideEdges() {
    var trackLeft = track.getBoundingClientRect().left;
    var base = track.scrollLeft;
    var pad = parseFloat(getComputedStyle(track).paddingLeft) || 0;
    var edges = [];
    // The track's direct element children ARE the slides — .shop-thumb cards on
    // the product rows, .trade-case figures on the trade page. Using children
    // rather than a fixed class keeps one scroller working across both.
    for (var i = 0; i < track.children.length; i++) {
      var slide = track.children[i];
      edges.push(
        Math.max(
          0,
          slide.getBoundingClientRect().left - trackLeft + base - pad,
        ),
      );
    }
    return edges;
  }

  // Nearest card edge, but never against the direction of travel unless we're
  // basically on that edge already (within TOL).
  function settleTargetLeft(dir, ref) {
    var cur = ref == null ? track.scrollLeft : ref;
    var edges = slideEdges();
    if (!edges.length) return null;
    var below = null;
    var above = null;
    edges.forEach(function (e) {
      if (e <= cur + 0.5 && (below === null || e > below)) below = e;
      if (e >= cur - 0.5 && (above === null || e < above)) above = e;
    });
    if (below === null) below = above;
    if (above === null) above = below;
    if (below === null) return null;
    var nearest = cur - below <= above - cur ? below : above;
    if (dir > 0 && nearest === below && cur - below > SETTLE_TOL) return above;
    if (dir < 0 && nearest === above && above - cur > SETTLE_TOL) return below;
    return nearest;
  }

  function animateSettle(target, gentle) {
    if (settleRaf) cancelAnimationFrame(settleRaf);
    var from = track.scrollLeft;
    var delta = target - from;
    // rAF is frozen in hidden tabs — land instantly rather than strand.
    if (Math.abs(delta) < 1 || document.hidden) {
      track.scrollLeft = target;
      return;
    }
    var duration = gentle
      ? Math.min(700, Math.max(400, Math.abs(delta) * 0.8))
      : Math.min(450, Math.max(200, Math.abs(delta) * 0.5));
    var power = gentle ? 4 : 3;
    var t0 = null;
    function step(ts) {
      if (t0 === null) t0 = ts;
      var p = Math.min(1, (ts - t0) / duration);
      var eased = 1 - Math.pow(1 - p, power);
      track.scrollLeft = from + delta * eased;
      if (p < 1) settleRaf = requestAnimationFrame(step);
      else settleRaf = null;
    }
    settleRaf = requestAnimationFrame(step);
  }

  function wheelLoop() {
    var d = wtarget - wpos;
    if (Math.abs(d) < 0.4 || document.hidden) {
      wpos = wtarget;
      track.scrollLeft = wpos;
      wheelRaf = null;
      return;
    }
    wpos += d * WHEEL_EASE;
    track.scrollLeft = wpos;
    wheelRaf = requestAnimationFrame(wheelLoop);
  }
  function ensureWheelLoop() {
    if (document.hidden) {
      wheelLoop();
      return;
    }
    if (wheelRaf == null) wheelRaf = requestAnimationFrame(wheelLoop);
  }
  function cancelWheel() {
    if (wheelRaf != null) {
      cancelAnimationFrame(wheelRaf);
      wheelRaf = null;
    }
    if (wheelIdle) {
      clearTimeout(wheelIdle);
      wheelIdle = null;
    }
  }

  // Card images/links are natively draggable — that ghost-drag fights us.
  track.addEventListener("dragstart", function (e) {
    e.preventDefault();
  });

  track.addEventListener("pointerdown", function (e) {
    if (!isDesktop() || e.button !== 0) return;
    down = true;
    moved = false;
    startX = e.clientX;
    startLeft = track.scrollLeft;
    activePointer = e.pointerId;
    if (settleRaf) {
      cancelAnimationFrame(settleRaf);
      settleRaf = null;
    }
    cancelWheel(); // a grab interrupts any wheel glide
  });

  track.addEventListener("pointermove", function (e) {
    if (!down) return;
    var dx = e.clientX - startX;
    if (!moved && Math.abs(dx) > 4) {
      moved = true;
      // Closed-hand cursor for the duration of the drag only — at rest the
      // cards keep their pointer cursor so they still read as links.
      track.classList.add("is-dragging");
      // Capture only once a real drag starts, so a plain click still reaches
      // the card's <a> and navigates normally.
      try {
        track.setPointerCapture(activePointer);
      } catch (err) {}
    }
    if (moved) {
      var next = startLeft - dx;
      // Direction recorded synchronously — scroll events lag and would be
      // stale at the settle instant.
      if (Math.abs(next - track.scrollLeft) > 0.5) {
        scrollDir = next > track.scrollLeft ? 1 : -1;
      }
      track.scrollLeft = next;
    }
  });

  function release() {
    if (!down) return;
    down = false;
    track.classList.remove("is-dragging");
    try {
      if (activePointer != null) track.releasePointerCapture(activePointer);
    } catch (err) {}
    activePointer = null;
    // A tap is left alone — the card link handles it.
    if (!moved) return;
    var left = settleTargetLeft(scrollDir);
    if (left != null) animateSettle(clampX(left));
  }
  track.addEventListener("pointerup", release);
  track.addEventListener("pointercancel", release);
  // A press that slips off the track would otherwise never release.
  document.addEventListener("pointerup", release);
  document.addEventListener("pointercancel", release);

  track.addEventListener(
    "wheel",
    function (e) {
      if (!isDesktop()) return;
      if (down) return; // dragging owns the motion
      // Horizontal intent only — vertical wheels scroll the page.
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();
      var unit =
        e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? track.clientWidth : 1;
      var dx = e.deltaX * unit;
      if (dx === 0) return;
      scrollDir = dx > 0 ? 1 : -1;
      if (settleRaf) {
        cancelAnimationFrame(settleRaf);
        settleRaf = null;
      }
      // Resync when idle in case something else moved the track.
      if (wheelRaf == null) {
        wpos = track.scrollLeft;
        wtarget = wpos;
      }
      wtarget = clampX(wtarget + dx * WHEEL_GAIN);
      ensureWheelLoop();
      // Retarget to the card edge shortly after input stops; the running ease
      // glides into it with no handoff.
      if (wheelIdle) clearTimeout(wheelIdle);
      wheelIdle = setTimeout(function () {
        wheelIdle = null;
        if (down) return;
        var edge = settleTargetLeft(scrollDir, wtarget);
        if (edge != null) {
          wtarget = clampX(edge);
          ensureWheelLoop();
        }
      }, 90);
    },
    { passive: false },
  );

  // A real drag must not follow the card's link. Capture phase so it beats the
  // anchor's navigation.
  track.addEventListener(
    "click",
    function (e) {
      if (!moved) return;
      moved = false;
      e.preventDefault();
      e.stopPropagation();
    },
    true,
  );
}

function initAllRowScrollers() {
  // Product rows plus the trade page's Case Studies row — same PDP-style
  // drag / wheel / settle. .trade-case slides are wider and vary in width,
  // which the child-based slideEdges handles the same as fixed-width cards.
  document
    .querySelectorAll(".product-row-track, .trade-cases-track")
    .forEach(initRowScroller);
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initAllRowScrollers);
} else {
  initAllRowScrollers();
}

// ─── PDP scroll cue ─────────────────────────────────────────────────────────
// The down-arrow button under #product (desktop) scrolls to the "You may also
// like" section (#recommend, a separate section below main-product). Its
// scroll-margin-top clears the fixed nav. Reduced motion jumps instead.
function initPdpScrollCue() {
  var cue = document.querySelector(".pdp-scroll-cue");
  if (!cue) return;

  // Duration of the glide, in ms. THE knob for how fast the cue scrolls —
  // native scrollIntoView({behavior:'smooth'}) has no duration option (browser
  // decides, ~300-500ms in Chrome), which is why this animates by hand.
  // Paired with the ease-out below: motion is front-loaded, so most of the
  // travel is done well before this elapses and the remainder is the settle.
  var SCROLL_MS = 700;

  cue.addEventListener("click", function () {
    var target = document.getElementById("recommend");
    if (!target) return;

    // Honour the CSS scroll-margin-top so the heading still lands clear of the
    // fixed nav — reading it here keeps the stylesheet the source of truth.
    var offset = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
    var startY = window.scrollY;
    var endY = Math.max(
      0,
      Math.min(
        document.documentElement.scrollHeight - window.innerHeight,
        target.getBoundingClientRect().top + startY - offset,
      ),
    );
    var delta = endY - startY;
    if (!delta) return;

    // Reduced motion, or a tab where rAF is frozen: land immediately rather
    // than stranding the page mid-scroll.
    var reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || document.hidden) {
      window.scrollTo({ top: endY, behavior: "auto" });
      return;
    }

    // THE fix for the "hesitates then catches up" feel. _base.scss sets
    // html{scroll-behavior:smooth} globally, so the browser runs its OWN
    // smooth animation toward every position this loop sets — it chases a
    // target that moves 60x a second and always lags behind. Passing
    // behavior:'auto' per call is not enough to suppress it. Turning the CSS
    // property off for the duration makes each frame land exactly where we put
    // it, which is what makes the motion track the curve below. Restored in
    // finish() so normal in-page anchor scrolling keeps its smoothing.
    var rootStyle = document.documentElement.style;
    var prevBehavior = rootStyle.scrollBehavior;
    rootStyle.scrollBehavior = "auto";

    var t0 = null;
    var cancelled = false;
    function finish() {
      rootStyle.scrollBehavior = prevBehavior;
      window.removeEventListener("wheel", stop);
      window.removeEventListener("touchstart", stop);
    }
    function stop() {
      cancelled = true;
      finish();
    }
    // A manual scroll mid-flight wins — never fight the user for the scrollbar.
    window.addEventListener("wheel", stop, { passive: true });
    window.addEventListener("touchstart", stop, { passive: true });

    function step(ts) {
      if (cancelled) return;
      if (t0 === null) t0 = ts;
      var p = Math.min(1, (ts - t0) / SCROLL_MS);
      // ease-in-out SINE — a gentle ramp up and a soft settle. Sine, not the
      // cubic/quart in-out used for the carousel settles: those cover only
      // ~6% of the distance in the first quarter of the time, which reads as a
      // hesitation over a scroll this long. Sine peaks at ~1.57x average
      // instead of 3x, so the ramp is short and the speed stays even.
      // (The old "hesitation" was the CSS scroll-behavior conflict handled
      // above, not the curve — but this stays the mildest option regardless.)
      var eased = -(Math.cos(Math.PI * p) - 1) / 2;
      window.scrollTo(0, startY + delta * eased);
      if (p < 1) requestAnimationFrame(step);
      else finish();
    }
    requestAnimationFrame(step);
  });
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initPdpScrollCue);
} else {
  initPdpScrollCue();
}

// ─── Announcement banner dismiss ────────────────────────────────────────────
// Markup: snippets/banner.liquid. The bar shows when <html> has .has-banner
// (set pre-paint by the inline script in theme.liquid). The × removes that
// class — hiding the bar and reverting --banner-h to 0 so the header +
// content slide back up — and records the dismissal keyed to the banner text,
// matching the inline script's check (so editing the text re-shows it).
document.addEventListener("DOMContentLoaded", function () {
  var banner = document.querySelector("[data-banner]");
  if (!banner) return;
  var closeBtn = banner.querySelector("[data-banner-close]");
  if (!closeBtn) return;
  closeBtn.addEventListener("click", function () {
    document.documentElement.classList.remove("has-banner");
    try {
      // Store the dismissed text + a timestamp. The pre-paint check in
      // theme.liquid re-shows the banner when the text changes OR when
      // 30 days have passed since this dismissal.
      localStorage.setItem(
        "slj-banner-dismissed",
        JSON.stringify({
          text: banner.getAttribute("data-banner-text") || "",
          ts: Date.now(),
        }),
      );
    } catch (e) {}
  });
});

// ─── Mobile menu (drill-in navigation) ──────────────────────────────────────
// The header MENU button (.main-menu) toggles body.menu-open, revealing the
// slide-out menu (snippets/nav.liquid / .mobile-menu). Tapping a parent row
// (.mobile-menu-parent) drills into its submenu view — body gets .menu-submenu
// and the header button flips CLOSE → BACK; BACK (or the toggle) returns to
// the root. One level deep. Labels swap purely in CSS (_layout.scss).
document.addEventListener("DOMContentLoaded", function () {
  var menuBtn = document.querySelector(".header-menu-toggle.main-menu");
  var menu = document.getElementById("nav-main-menu");
  if (!menuBtn || !menu) return;

  var views = menu.querySelectorAll(".mobile-menu-view");

  // Activate a view by its data-view key ("root" or a submenu key).
  function setView(key) {
    Array.prototype.forEach.call(views, function (v) {
      v.classList.toggle("is-active", v.getAttribute("data-view") === key);
    });
    document.body.classList.toggle("menu-submenu", key !== "root");
    menu.scrollTop = 0;
  }

  function openMenu() {
    setView("root");
    document.body.classList.add("menu-open");
  }
  function closeMenu() {
    document.body.classList.remove("menu-open");
    setView("root"); // reset so it reopens at the root
  }
  window.closeMobileMenu = closeMenu;

  menuBtn.addEventListener("click", function (e) {
    e.preventDefault();
    if (!document.body.classList.contains("menu-open")) {
      openMenu();
    } else if (document.body.classList.contains("menu-submenu")) {
      setView("root"); // BACK
    } else {
      closeMenu(); // CLOSE
    }
  });

  menu.addEventListener("click", function (e) {
    // Parent row → drill into its submenu (no navigation).
    var parentBtn = e.target.closest(".mobile-menu-parent");
    if (parentBtn) {
      setView(parentBtn.getAttribute("data-target"));
      return;
    }
    // A real link tap navigates — drop the open state so it doesn't linger.
    if (e.target.closest("a")) closeMenu();
  });

  document.addEventListener("keydown", function (e) {
    if (e.key !== "Escape") return;
    if (document.body.classList.contains("menu-submenu")) setView("root");
    else closeMenu();
  });
});

// ── Editorial page: "Shop the edit" bar + product overlay ────────────────────
// sections/main-editorial.liquid. The bar is fixed to the bottom of the
// viewport and hides while the footer is in view (it would sit on top of it).
// It opens a full-screen overlay holding the page's product grid, which is
// kept in a <template> until first open so its images don't load with the
// page. Close: the CLOSE button or Escape. Body scroll is locked while open
// via html.editorial-overlay-open (_editorial.scss).
document.addEventListener("DOMContentLoaded", function () {
  var bar = document.querySelector("[data-editorial-bar]");
  var overlay = document.querySelector("[data-editorial-overlay]");
  if (!bar || !overlay) return;

  // Both are position:fixed and must sit above the site header (z 200) and
  // everything else on the page. Inside the section they'd be trapped by any
  // ancestor stacking context (a transform/opacity on a wrapper is enough), so
  // re-parent them to <body> where only z-index decides.
  document.body.appendChild(overlay);
  document.body.appendChild(bar);

  var tpl = document.getElementById("editorial-overlay-grid");
  var body = overlay.querySelector("[data-editorial-overlay-body]");
  var closeBtn = overlay.querySelector("[data-editorial-close]");
  var footer = document.querySelector("footer");
  var lastFocus = null;

  // Open/close is a 120ms ease-out dissolve (the Figma interaction). The
  // overlay is un-hidden first, a reflow is forced so that state is painted,
  // then .is-open fades it in; on close the class comes off and `hidden`
  // returns once the fade has run.
  var FADE_MS = 120; // keep in sync with $editorial-fade in _editorial.scss
  var hideTimer = null;

  function openOverlay() {
    if (tpl && body && !body.firstElementChild) {
      body.appendChild(tpl.content.cloneNode(true));
    }
    clearTimeout(hideTimer);
    lastFocus = document.activeElement;
    overlay.hidden = false;
    overlay.scrollTop = 0;
    void overlay.offsetWidth;
    overlay.classList.add("is-open");
    document.documentElement.classList.add("editorial-overlay-open");
    if (closeBtn) closeBtn.focus();
  }

  function closeOverlay() {
    overlay.classList.remove("is-open");
    document.documentElement.classList.remove("editorial-overlay-open");
    clearTimeout(hideTimer);
    hideTimer = setTimeout(function () {
      overlay.hidden = true;
    }, FADE_MS);
    if (lastFocus && typeof lastFocus.focus === "function") lastFocus.focus();
  }

  bar.addEventListener("click", openOverlay);
  if (closeBtn) closeBtn.addEventListener("click", closeOverlay);
  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" && !overlay.hidden) closeOverlay();
  });

  if (footer && "IntersectionObserver" in window) {
    new IntersectionObserver(function (entries) {
      bar.classList.toggle("editorial-bar--hidden", entries[0].isIntersecting);
    }).observe(footer);
  }
});
